/**
 * Theme resolution: Intensity preferences + colour scheme -> concrete tokens.
 *
 * Authorised by: design-direction §2 (Drystone surface / Waymark structure, with the
 * legibility tiebreaker), §3 (colour architecture, temperature as value-and-warmth),
 * §4 (typography, density, spatial grammar), §5 (motion vocabulary, haptics, celebration).
 *
 * TWO RULES GOVERN THIS FILE.
 *
 * 1. **Level 0 is the reference; 1–4 are derivations.** (design-direction §3 objection 2,
 *    implementation-brief §9.) This is now literal, not aspirational: `REFERENCE_TOKENS`
 *    holds the level-0 values, `STEP_TOKENS` holds one unit of delta, and
 *    `STEP_MULTIPLIERS` declares how many units each level is worth. Change the reference
 *    and every level moves with it. The places where a level genuinely cannot be a
 *    multiple are listed in `REFERENCE_EXCEPTIONS`, each with its own test.
 *
 * 2. **Contrast is stated as a target, never picked by eye.** Every colour that carries an
 *    obligation — a state hairline, a state mark, the accent, the compost gradient — is
 *    computed by `atContrast`/`atLuminance` from the ratio it owes. Hand-picking indices
 *    out of a 12-step ramp is what produced a dark theme in which `closed` and
 *    `composting` were the same colour (1.00:1) and the compost gradient was invisible;
 *    the ramps simply have no stops inside the narrow luminance band that the fixed text
 *    colours leave available. `colour.ts` explains the arithmetic.
 */

import {
  type LabelDensity,
  type MaterialId,
  type ReadingFamily,
  type Temperature,
  type Treatment,
  DEFAULT_MATERIAL,
  isReadingFamily,
  normaliseMaterial,
  normaliseTreatment,
} from '@carn/core';

import { slate } from './palette';
import { MATERIAL_TOKENS, type MaterialRamps, type MaterialTokens } from './materials';
import { relativeLuminance } from './contrast';
import { atContrast, atLuminance, luminanceForContrast, mix } from './colour';
import {
  type ClampedChannel,
  type IntensityChannel,
  type IntensityLevel,
  type IntensityPreferences,
  type SystemAccessibility,
  applySystemAccessibility,
  normaliseIntensity,
  resolveChannels,
} from './intensity';

export type ColourScheme = 'light' | 'dark';

/**
 * `Temperature` is re-exported from `@carn/core` and is NOT declared here.
 *
 * It used to be a second, independent five-member union in this file. Two copies of the
 * product's core state vocabulary can drift to different arities, and the law
 * ("`Temperature` has exactly five members and never gains a sixth", CLAUDE.md) can then
 * be true in one package and false in the other. `@carn/core` is pure and platform-free,
 * so depending on it from the token layer costs nothing.
 */
export type { Temperature };

/**
 * `LabelDensity` is re-exported from `@carn/core` rather than redeclared, for the same
 * reason `Temperature` is (see above): the vocabulary lives in one place, so `Waymark`
 * and `Button` cannot read a type that has drifted from `Settings.labels`.
 */
export type { LabelDensity };

// ---------------------------------------------------------------------------
// Contrast obligations — the numbers the colour tables are solved against
// ---------------------------------------------------------------------------

/**
 * Targets carry a margin over the WCAG threshold they satisfy (4.7 for a 4.5 gate, 3.2
 * for a 3.0 gate). The margin absorbs the rounding to 8-bit sRGB, so the assertions in
 * `test/` can use the real WCAG numbers and still hold.
 */
const TARGET = {
  /** Body text on any surface. WCAG AA. */
  text: 4.7,
  /** A state MARK against the surface it sits on: an icon tint read as small text. */
  mark: 4.7,
  /** The stroke/text-safe accent against the page. */
  accentMark: 5.5,
  /** The stroke/text-safe accent against any temperature surface it may be drawn on. */
  accentMarkOnCard: 3.2,
  /** A tinted fill that text will be drawn on top of, against the page. */
  soft: { light: 1.25, dark: 1.6 },
} as const;

/**
 * `background` vs `backgroundDeep`, by decor level.
 *
 * design-direction §4's Critic Record calls sparsity-hides-information "the strongest
 * objection in this document" and resolves it with, among other things, "the
 * downward-visible compost gradient at screen bottom (a sliver of what's below is always
 * perceptible — the interface admits depth)". That makes the gradient SEMANTIC, not
 * decorative, so it has a floor at decor level 0 as well: the sliver is perceptible at
 * the reference rendering too. The decor channel only makes it deeper.
 *
 * It previously measured 1.00:1 in dark mode at the shipped default, because `ember[11]`
 * and `slate[11]` have identical relative luminance.
 */
const DEEP_RATIO: Record<IntensityLevel, number> = {
  0: 1.26,
  1: 1.31,
  2: 1.36,
  3: 1.41,
  4: 1.46,
};

// ---------------------------------------------------------------------------
// The `contrast` channel (design-direction §3, RULINGS R10, closing DEVIATIONS D23)
// ---------------------------------------------------------------------------

/**
 * THE CHANNEL WITH A FLOOR INSTEAD OF A ZERO, and the three properties that make that
 * coherent with a dial whose whole job is to scale stimulation.
 *
 *   1. A FLOOR AT EVERY LEVEL. Level 0 of every table below already clears WCAG 1.4.11's
 *      3:1 (the 3.2 carries the 8-bit rounding margin the file header describes). Level 0 is
 *      the REFERENCE rendering (design-direction §3 objection 2), and a reference rendering
 *      that fails a success criterion is not a reference. Every other channel's level 0 is
 *      "off"; this one's is "the legal minimum".
 *   2. A USER-CONTROLLABLE RANGE ABOVE IT. The ladders rise monotonically, and the channel is
 *      per-channel overridable like every other (§3 objection 1), so `{ dial: 0, overrides:
 *      { contrast: 4 } }` is near-monochrome, still, silent — and hard-edged. That is the
 *      combination the dial alone could never express and the one product-concept §8.2's
 *      "high-contrast" shopping mode is specified in terms of.
 *   3. TEXT IS UNTOUCHED. Nothing below is read by `typography`, by `text`/`textSecondary`/
 *      `textTertiary`/`textDisabled`, or by `temperature[t].mark` (an icon tint held at the
 *      TEXT target, 4.7, at every level, because a state mark is read like small text).
 *      design-direction §3 fixes text contrast at accessible levels at every setting with no
 *      carve-out, and `test/contrast.test.ts` asserts every text pair is byte-identical across
 *      the whole contrast range.
 *
 * WHY `border` AND `borderStrong` BOTH RISE AND STAY APART. `border` used to be a 1.8:1 "soft
 * depth cue" — but `Surface` paints it as the boundary of every card, `LoopCard` as the edge of
 * the capsule photo, `CompostGradient` as the top of the compost sliver and `Button` as the
 * boundary of a disabled control. It is a component boundary wherever it is actually used, so
 * it owes 3:1. `borderStrong` stays a rung above it at every level, so the hierarchy the soft
 * hairline was there to express survives the floor rather than being flattened by it.
 *
 * EVERY RATIO BELOW IS AGAINST THE HARDEST FILL THE TOKEN IS PAINTED ON, which is what
 * `BOUNDARY_GROUNDS` enumerates — not against the page. In the dark scheme that is the warm
 * card, so the realised ratio against the *page* is much higher than the number in the table
 * (7.5:1 at level 0 rather than 3.2:1). That is the arithmetic consequence of one token being
 * painted on nine fills, and it is the direction design-direction §2's tiebreaker settles: "where
 * the two conflict resolves to Waymark, because legibility is an accessibility requirement and
 * mood is not."
 */
const BOUNDARY_CONTRAST: Record<IntensityLevel, number> = {
  0: 3.2,
  1: 3.6,
  2: 4.2,
  3: 5.0,
  4: 6.0,
};

/**
 * `borderStrong` — a rung above `border` at every level.
 *
 * THE TOP OF THIS LADDER IS SET BY ARITHMETIC, NOT BY TASTE. Both boundary tokens are solved
 * against the hardest fill they are painted on (`BOUNDARY_GROUNDS`), and in the dark scheme that
 * is the warm card at relative luminance 0.088 — against which *no colour that exists* can
 * exceed `1.05 / 0.138 = 7.61:1`. The previous 9.0 was therefore a target the theme could not
 * reach: `atLuminance` clamped it to `#FFFFFF` and realised 7.56:1 at level 4 and 7.49:1 at
 * level 3, so the two top rungs were 0.07 apart while the table claimed 1.5. A target table that
 * a resolved theme cannot land on is a table that lies about what ships (R80), so the ladder is
 * restated at values every rung actually reaches, with headroom left over.
 */
const STRONG_BOUNDARY_CONTRAST: Record<IntensityLevel, number> = {
  0: 4.5,
  1: 5.0,
  2: 5.6,
  3: 6.3,
  4: 7.1,
};

/**
 * A temperature card's hairline against ITS OWN surface. Solved per card rather than against
 * the page, because that is where WCAG 1.4.11 places the obligation and where DEVIATIONS D7
 * discharged it when the five-rung surface ladder turned out to be arithmetically unable to
 * carry 3:1 between adjacent rungs.
 */
const TEMPERATURE_BOUNDARY_CONTRAST: Record<IntensityLevel, number> = {
  0: 3.2,
  1: 3.6,
  2: 4.2,
  3: 5.0,
  4: 6.0,
};

/**
 * The GROUND EDGE (RULINGS R56): the hairline and inset band at the head of any surface
 * painted in `backgroundDeep`, and at the sliver that is its top edge on the home surface.
 *
 * design-direction §4 calls the vertical axis autism-critical — "Compost is *literally
 * downward*… learned once and true forever". In the light scheme `backgroundDeep` is a clear
 * step DOWN in value and the metaphor is legible on colour alone. In the dark scheme it is, by
 * design, a faint warm LIFT ("nothing can be darker than the night ground" — see `GROUND`),
 * so the same step is nearly invisible: a metaphor legible in one scheme and not the other is
 * learned once and true half the time.
 *
 * The fix is NOT to darken the night ground, which would cost the ember warmth
 * design-direction §2 explicitly authorises. It is to carry the step with WEIGHT: a hairline
 * several times the ordinary one, plus a wider band inset under it. Consumed by the compost
 * surface's head and by the sliver, so the edge you see and the layer you land on are the same
 * object.
 *
 * SOLVED AGAINST THE BAND, NOT AGAINST THE DEEP GROUND (RULINGS R101). This table used to be
 * applied against `backgroundDeep`, and `GroundEdge` never paints the hairline next to it: the
 * hairline is `borderTopWidth` on a `View` whose own fill is `bandColour`, so its lower
 * neighbour is the band and its upper neighbour is the page or — on the compost surface's head,
 * where the edge sits at y=0 — nothing at all. Measured against its real neighbour it was
 * **2.51:1 in dark and 2.56:1 in light at level 0**, under 3:1 for 67 of the 250 resolved
 * themes, on the object whose entire job is to make design-direction §4's autism-critical
 * vertical axis legible. R56 is not met in dark mode without this correction.
 */
const GROUND_EDGE_CONTRAST: Record<IntensityLevel, number> = {
  0: 3.2,
  1: 3.6,
  2: 4.2,
  3: 5.0,
  4: 6.0,
};

/**
 * The edge's weight in px, and the band's height in px. Stated absolutely rather than as a
 * multiple of `BOUNDARY_WIDTH`, because multiplying two rising tables produced an 11px hairline
 * at level 4 — which is a slab, not an edge. Three times the level-0 hairline is already a
 * landmark; the ladder above it is a nudge, not a doubling. The band is always wider than the
 * hairline, which is the half of R56's "a strong top hairline plus a WIDER inset band" that a
 * single number could not express.
 */
const GROUND_EDGE_WIDTH: Record<IntensityLevel, number> = { 0: 3, 1: 3.5, 2: 4, 3: 5, 4: 6 };
const GROUND_EDGE_BAND: Record<IntensityLevel, number> = { 0: 6, 1: 7, 2: 8, 3: 10, 4: 12 };

/**
 * Boundary WEIGHT, in px, by contrast level. `space.hairline` — which every card, row,
 * checkbox outline and divider in the app already reads.
 *
 * Two channels for one property, deliberately: a boundary is easier to find when it is both
 * darker and thicker, and weight is the channel that survives a colour-blind reading, a
 * greyscale screenshot and a low-quality LCD. `space.strokeWidth` is NOT in this table —
 * design-direction §6 fixes ONE stroke weight across the whole icon system, so the glyphs keep
 * theirs whatever the dial says.
 */
const BOUNDARY_WIDTH: Record<IntensityLevel, number> = { 0: 1, 1: 1, 2: 1.5, 3: 2, 4: 2.5 };

/**
 * Focus-ring WEIGHT by contrast level. The ring's COLOUR is held fixed at every setting
 * (`GROUND.focusRing`, RULINGS R61) because it is the one affordance a keyboard or
 * switch-control user navigates by, and it must not become a variable. Weight is the part it
 * is safe — and useful — to let the user raise.
 */
const FOCUS_WIDTH: Record<IntensityLevel, number> = { 0: 2, 1: 2.25, 2: 2.5, 3: 3, 4: 3.5 };

/**
 * The accent's contrast against the page, by colour level.
 *
 * DOCUMENTED EXCEPTION to rule 1 (see `REFERENCE_EXCEPTIONS`): a colour is not a scalar,
 * so the accent ramp cannot be "the reference times a multiplier". It is a declared
 * ladder — but a MONOTONIC one, in both schemes, which is the property that was broken.
 *
 * The light scheme used to run 11.52 → 9.29 → 5.84 → 1.90 → 1.54 as the dial went up: the
 * accent became *less* discriminable exactly as the dial promised more salience, and at
 * Lively and Vivid it failed WCAG 1.4.11's 3:1 for a graphical object. design-direction
 * §2's own tiebreaker settles it — "where the two conflict resolves to Waymark, because
 * legibility is an accessibility requirement and mood is not" — so the dial now expresses
 * rising salience as rising SATURATION (`CHROMA_BY_LEVEL`) *and* rising contrast, and the
 * light-scheme accent is a deep gorse fill with light text rather than a bright gorse fill
 * that cannot be seen against a wool ground. §2 objection 2's "marketing surfaces may run
 * the accent hotter than the in-app default" is where the bright stop still lives.
 */
const ACCENT_CONTRAST: Record<ColourScheme, Record<IntensityLevel, number>> = {
  light: { 0: 4.0, 1: 4.4, 2: 4.9, 3: 5.5, 4: 6.2 },
  dark: { 0: 5.0, 1: 5.8, 2: 6.8, 3: 8.0, 4: 9.4 },
};

/**
 * How much of the material's hue survives, by colour level. This is the "colour
 * saturation" the dial claims to scale (design-direction §3).
 *
 * At level 0 the weight is 0: every temperature colour and the accent collapse onto the
 * single neutral anchor, so the reference rendering is genuinely near-monochrome — a
 * value-only encoding from one ramp — which is what the copy has always promised and the
 * tables never delivered.
 */
const CHROMA_BY_LEVEL: Record<IntensityLevel, number> = { 0: 0, 1: 0.35, 2: 0.6, 3: 0.8, 4: 1 };

/**
 * The one ramp the achromatic rendering is drawn from — PER MATERIAL.
 *
 * At colour level 0 every temperature colour and the accent collapse onto this single anchor, so
 * the reference rendering is a value-only encoding from one hue axis. Making the anchor
 * per-material is what keeps that rendering *this material's* near-monochrome rather than
 * everyone's: Drystone's level 0 is a blue-grey, Calch's a warm stone grey, Glo's a blue-black.
 *
 * WHY THAT MATTERS RATHER THAN BEING A FLOURISH. design-direction §3 objection 2 makes low-stim
 * the REFERENCE rendering and refuses to let it be "the neglected afterthought". A materials
 * feature that evaporated at colour level 0 would tell every low-stim user that the reference
 * rendering is the one place the design system stops caring — and, since Plus sells materials,
 * would sell them a thing that switches off at the setting they actually use. The near-monochrome
 * end is still near-monochrome; it is a different monochrome.
 *
 * `ACHROMATIC_ANCHOR` remains exported at its original value (Drystone's) because it is part of
 * the package's published surface and Drystone is the default; `achromaticAnchor` is the honest
 * form and is what `theme.ts` itself uses.
 */
export const ACHROMATIC_ANCHOR = slate[6];

export function achromaticAnchor(material: MaterialId = DEFAULT_MATERIAL): string {
  return MATERIAL_TOKENS[material].ramps.neutral[6];
}

// ---------------------------------------------------------------------------
// Colour
// ---------------------------------------------------------------------------

export type TemperatureColours = {
  /** Surface fill for a loop card at this temperature. Value carries depth. */
  surface: string;
  /**
   * Hairline. Waymark discipline: one stroke weight. Held at ≥3:1 against its own
   * surface (WCAG 1.4.11) — it is the card's boundary, so it has to be visible. It used
   * to sit at 1.2–1.8:1 under a comment claiming "colour carries the state".
   */
  border: string;
  /**
   * Small mark colour (icon tint), at ≥4.5:1 against its own surface.
   *
   * The mark is where the colour channel actually carries state, and even here it is
   * REDUNDANT by law: `TEMPERATURE_GLYPH` (core `model.ts`) gives the shape and
   * `temp.*` (copy catalogue) gives the word. Colour is the third channel, never the
   * only one (design-direction §3).
   */
  mark: string;
};

/**
 * The hue each state borrows, and the order they sit in on the value axis.
 *
 * design-direction §3: temperature is "value and warmth, never alarm hues" — "warm loops
 * slightly lighter/warmer, composting loops receding toward the background colour". The
 * ladder below is that sentence: `warm` is the lightest card and rises above the page
 * ground, `cooling` sits level with it, and the rest recede past it. In the dark scheme
 * the same ordering holds; only the direction of "away from the surface" flips.
 *
 * `closed` and `released` share a depth in core's `TEMPERATURE_DEPTH` (both terminal, both
 * successes — CLAUDE.md: "`released` is a success state, equal in standing to `closed`"),
 * but they are still two different states, so they get two different rungs and two
 * different hues.
 *
 * `Record<Temperature, string>` is a compile-time exhaustiveness check: the table cannot
 * miss a state and cannot invent a sixth one.
 */
function temperatureHue(r: MaterialRamps): Record<Temperature, string> {
  return {
    warm: r.warm[6],
    cooling: r.cool[6],
    composting: r.quiet[6],
    closed: r.living[6],
    released: r.ground[6],
  };
}

/**
 * Lightest card first. Exported so a test can assert one rung per `TEMPERATURES` member
 * from `@carn/core` and no more — the two vocabularies cannot drift to different arities.
 */
export const TEMPERATURE_ORDER: readonly Temperature[] = [
  'warm',
  'cooling',
  'composting',
  'closed',
  'released',
] as const;

export type Colours = {
  /** Page ground. */
  background: string;
  /** The compost gradient's far end — always faintly perceptible at screen bottom (§4). */
  backgroundDeep: string;
  surface: string;
  surfaceRaised: string;
  /**
   * The ordinary boundary hairline. ≥3:1 against **every fill it is painted on** at EVERY
   * contrast level (WCAG 1.4.11, RULINGS R10/R99) — see `BOUNDARY_GROUNDS`, which is the
   * enumeration, and `boundaryGroundLuminance`, which solves against the hardest member.
   */
  border: string;
  /** A boundary a rung stronger. Same ground set, same solve, one rung up. */
  borderStrong: string;
  /**
   * THE CAIRN'S STONE MATERIAL (design-direction §2: Drystone owns surface — "weathered
   * oak"; palette.ts documents oak as "Tactile accents, capsule stone, cairn"; ADA review
   * V1, recorded in docs/DEVIATIONS.md).
   *
   * The stones used to be painted in `border`/`borderStrong` — neutral boundary tokens —
   * so the colour channel of the dial never reached the brand's central object: at Vivid
   * the cairn was exactly as grey as at Still. This pair is the same solve as those two
   * tokens (same ladders, same hardest-ground obligation, same 3:1 floor at every level —
   * see `BOUNDARY_GROUNDS`), anchored on the material's WARM ramp through the same
   * `tint()` machinery every temperature surface uses. At colour level 0 the tint weight
   * is 0, so both collapse onto the neutral anchor and the reference rendering is exactly
   * what it always was; the dial only ever adds the warmth back.
   */
  stoneFill: string;
  /** The newest stone: one rung stronger, exactly as `borderStrong` is to `border`. */
  stoneFillStrong: string;
  /**
   * THE RECOGNITION PHOTO'S FRAME, ON THE LEADING WARM CARD ONLY (RULINGS R157).
   *
   * product-concept §6 objection 3 asks for capsule imagery "framed... warmly (not as
   * evidence)", and R157 spends the fold budget an uncramped viewport leaves unspent on
   * exactly that: the photo grows to 2×`minTarget` and its neutral `border` hairline is
   * replaced by this one. design-direction §2 gives Drystone "weathered oak", and
   * `palette.ts` records oak as "tactile accents, capsule stone, cairn" — so the warm frame
   * is the material's own ramp rather than a colour invented for one component.
   *
   * SOLVED EXACTLY AS `border` IS, ANCHORED ON THE MATERIAL'S WARM RAMP — the same solve
   * `stoneFill` uses, and it lands on `stoneFill`'s value by construction today. It is its
   * own token for the reason `textDisabled` is its own token beside `textTertiary`: a call
   * site declares WHY it is warm (this is a frame around a photograph, not a stone in a
   * cairn), and if the two ever have to diverge the call sites already say which is which.
   * At colour level 0 the tint weight is 0, so it collapses onto the neutral anchor and the
   * reference rendering is the hairline it replaced; the dial only ever adds the warmth.
   */
  photoFrame: string;
  /**
   * The hairline at the edge of the deep ground (RULINGS R56). Solved against
   * `elevation.groundEdge.bandColour` — the fill `GroundEdge` actually paints it on — not
   * against `backgroundDeep`, which it never touches (RULINGS R101).
   */
  groundEdge: string;
  /** Text ramp. FIXED across all intensity levels — including the whole contrast range. */
  text: string;
  textSecondary: string;
  textTertiary: string;
  /**
   * Text on a disabled control. SOLVED against the same ≥4.5:1 target as body text
   * (RULINGS R12), not dimmed.
   *
   * `opacity: 0.55` on a disabled control multiplies through to its label and took it below AA
   * on the two surfaces with the most disabled controls in the product. design-direction §3
   * fixes text contrast at accessible levels at every setting and grants no exception for a
   * disabled state — and the control a user cannot use is precisely the one they most need to
   * be able to READ, because otherwise they cannot tell what they are being denied from a bug.
   * Disablement is signalled by fill, boundary and `accessibilityState.disabled`; never by
   * contrast loss. Where a surface can, it should HIDE the control instead of disabling it.
   */
  textDisabled: string;
  /** Text on a filled accent. */
  onAccent: string;
  /** The focus accent, as a FILL. ≥3:1 against the page at every level, both schemes. */
  accent: string;
  /**
   * The focus accent as a STROKE or as text: an accent guaranteed ≥4.5:1 against the page
   * and ≥3:1 against every temperature surface, so a 1.75px outline or an accent-coloured
   * word is legible at every level. Split out from `accent` because a fill and a hairline
   * have different contrast obligations and one value cannot honestly serve both.
   */
  accentMark: string;
  /** Accent at low value contrast, for tinted fills behind text. */
  accentSoft: string;
  /**
   * `accent`'s SALIENCE, from `neutral`'s hue rather than `accent`'s own (RULINGS R175). Same
   * anchor as every other neutral token (`neutral`, the material's achromatic ramp stop — never
   * `accentAnchor`, which is what carries the accent's hue), solved against the SAME ground and
   * the SAME `ACCENT_CONTRAST` ladder `accent` is, so it tracks `accent`'s salience level for
   * level — a fill that draws exactly as much attention as the accent would, never more, never a
   * fixed amount less. Swept across all six materials, both schemes, every colour level and both
   * contrast extremes (600 measurements), the worst gap between its contrast against `surface`
   * and `accent`'s is 0.126 (llanw, light, colour 2) — negligible.
   *
   * NOT A HUE GUARANTEE. `neutral` and `accentAnchor` are different anchors, but "different
   * anchor" is not "far apart on every material" — a material whose own neutral ramp and accent
   * ramp are close in hue (llanw, by that material's own design) puts this token and `accent`
   * only 0.3°–1.9° apart in hue at colour 1 (ΔE00 4.3–6.2, barely past "the same colour" —
   * `deltaE2000`'s own doc comment gives ~2.0 as that threshold). Drystone is the far case (hue
   * distance 165.7°–172.1° at colour 1–4) and is not representative of the other five materials.
   * See `docs/OPEN-QUESTIONS.md` ("Materials: on llanw the resting-surface fill converges back
   * toward the accent's hue") for the open question this leaves.
   *
   * `CHROMA_BY_LEVEL[0] === 0` means `accentAnchor === neutral` at colour level 0 (see
   * `colours()`), so this token and `accent` are solved from the identical anchor with the
   * identical target THERE — byte-identical, not merely both-achromatic, confirmed across all
   * 300 combinations of material × scheme × decor × contrast at colour 0. That is what makes
   * R175's "at dial 0 the reference rendering is untouched" true by construction rather than
   * an argued approximation: swapping `accent` for this token on a resting surface changes
   * nothing to look at until the colour channel actually has somewhere to put a hue.
   *
   * `TokenRole` in `packages/ui/test/boundary-grounds.test.ts` classifies this `'chosen'`,
   * the same as `accent`: a control filled with it picks its own boundary at render time
   * (`boundaryOn`/`glyphOn`) rather than being solved against a fixed boundary ground, for
   * the reason Guard E gives for `accent` itself — this moves with the `colour` channel in
   * both directions (confirmed: it is unaffected by `contrast` — at colour 2 light, contrast 0
   * and contrast 4 both resolve to `#59636D`) and no fixed token can straddle it either.
   */
  accentNeutral: string;
  /** Focus ring for keyboard/switch-control navigation. Never scaled by intensity. */
  focusRing: string;
  temperature: Record<Temperature, TemperatureColours>;
};

/**
 * The fixed part of a scheme: the grounds and the text ramp. Level-independent by law
 * (design-direction §3: "text contrast is fixed at accessible levels"; the dial scales
 * decorative contrast only).
 */
type SchemeGround = {
  background: string;
  surface: string;
  surfaceRaised: string;
  text: string;
  textSecondary: string;
  textTertiary: string;
  onAccent: string;
  focusRing: string;
  /** Which way a colour must move to gain contrast against this scheme's surfaces. */
  away: 'lighter' | 'darker';
  /** Hue of the deep compost ground at decor 0, and at decor 1–4. */
  deepHue: { reference: string; raised: string };
  /** Chroma ceiling for the deep ground — see the note on `ember` below. */
  deepChroma: number;
};

/**
 * The scheme grounds, BY ROLE INDEX rather than by hand-picked ramp stop.
 *
 * Every index below was previously a literal (`wool[3]`, `slate[11]`, …). Reading them off a
 * material's roles instead is the whole mechanism: a material supplies eight ramps, this
 * function turns them into a scheme, and nothing downstream knows which material it is looking
 * at. The indices themselves are unchanged, so `groundFor(scheme, drystone)` reproduces the old
 * table exactly — asserted in `test/materials.test.ts`.
 */
function groundFor(scheme: ColourScheme, material: MaterialTokens): SchemeGround {
  const r = material.ramps;
  if (scheme === 'light') {
    return {
      /**
       * The light ground is `ground[3]`, not `ground[1]`.
       *
       * design-direction §3 puts `composting` "receding toward the background colour", which
       * only reads if the page ground sits low enough on the value axis for cards to rise
       * above it and sink below it. On a near-white ground every card was within 1.10:1 of
       * every other card and of the page. A weathered putty ground is also the Drystone read
       * (matte, tactile, low-gloss — §2) and is easier on light sensitivity.
       */
      background: r.ground[3],
      surface: r.ground[1],
      surfaceRaised: r.ground[0],
      text: r.neutral[11],
      textSecondary: r.neutral[8],
      textTertiary: r.neutral[7],
      onAccent: r.ground[0],
      // Its own value, distinct from `accent` at every level: the ring used to be
      // `neutral[9]` and the level-0 light accent was also `neutral[9]`.
      focusRing: r.neutral[10],
      away: 'darker',
      // Nothing red is anywhere near the light ground, so the compost tint runs at full
      // strength: the ground ramp at the reference rendering, the quiet ramp above it.
      deepHue: { reference: r.ground[6], raised: r.quiet[6] },
      deepChroma: 1,
    };
  }
  return {
    background: r.neutral[11],
    surface: r.neutral[10],
    surfaceRaised: r.neutral[9],
    text: r.ground[1],
    textSecondary: r.neutral[3],
    textTertiary: r.neutral[4],
    onAccent: r.neutral[11],
    focusRing: r.ground[0],
    away: 'lighter',
    /**
     * design-direction §2: "Hearth survives only as the top end of the darkness range:
     * the lowest-stimulation night theme may borrow its ember-on-dark warmth." So the glow
     * belongs at decor 0 — the reference rendering — and the neutral at 1–4. The old code had
     * it exactly inverted (`decor === 0 ? slate[10] : ember[11]`).
     *
     * Every material's `glow` is an orange-red ramp and "red is never a state" (§3). The
     * authorised use is a warm HINT on a near-black ground, so `nightChroma` keeps the resolved
     * token's absolute chroma under `RED_CHROMA_FLOOR`; a saturated glow fill would fail the
     * `isRed` scan in `test/`. Putting the glow at decor 0, where the deep ground is at its
     * darkest, makes that constraint easier rather than harder.
     */
    deepHue: { reference: r.glow[6], raised: r.neutral[6] },
    deepChroma: material.nightChroma,
  };
}

/**
 * Tint an anchor hue by the colour channel. Weight 0 returns the material's own neutral axis.
 *
 * `anchor` is a parameter rather than a module constant because the achromatic axis is
 * per-material — see `achromaticAnchor` for why that is a requirement of design-direction §3
 * objection 2 rather than a nicety.
 */
function tint(anchor: string, hue: string, colour: IntensityLevel, ceiling = 1): string {
  return mix(anchor, hue, CHROMA_BY_LEVEL[colour] * ceiling);
}

/**
 * The five surface luminances, geometrically spaced across the whole band the fixed text
 * colours leave available.
 *
 * The band is DERIVED from the text law, not chosen: the extreme rung is placed exactly
 * where `textSecondary` still clears AA on it. That is why the text-contrast tests and
 * the surface-separation tests cannot be made to disagree — one is the boundary condition
 * of the other.
 */
function surfaceBand(ground: SchemeGround): { top: number; bottom: number } {
  const secondary = relativeLuminance(ground.textSecondary);
  if (ground.away === 'darker') {
    // Light: the darkest card is as dark as AA allows; the lightest is near-white.
    return { top: 0.96, bottom: luminanceForContrast(secondary, TARGET.text, 'lighter') };
  }
  // Dark: the lightest card is as light as AA allows; the darkest is a near-black that
  // still sits below the night ground, so the deepest states recede past the page.
  return { top: luminanceForContrast(secondary, TARGET.text, 'darker'), bottom: 0.004 };
}

/**
 * `textTertiary`, SOLVED — and solved from the BAND GEOMETRY rather than from the realised
 * surface hexes, so it does not vary with the colour or decor channel.
 *
 * Two laws collide here and both have to hold:
 *
 *   1. design-direction §3 — text contrast is fixed at accessible levels at EVERY
 *      setting. There is no carve-out for a third tone.
 *   2. The same section — the dial scales DECORATIVE contrast only, never text. The
 *      token tests assert the three text tones are byte-identical across all five levels,
 *      which the foundation critique singled out as one of the few genuinely structural
 *      guarantees in the package.
 *
 * Deriving tertiary from the rendered card colours satisfied (1) and broke (2), because
 * `atLuminance` cannot land exactly on a target for a saturated anchor, so the realised
 * surface luminance drifts a little per colour level and dragged tertiary with it.
 *
 * Solving against the band's theoretical extreme satisfies both: the extreme is a pure
 * number, fixed by `surfaceBand` and `DEEP_RATIO`, independent of hue.
 *
 * (The hand-picked `slate[7]` / `slate[4]` this replaces measured 3.47–3.54:1 on
 * `temperature.warm` and `backgroundDeep` — below AA. The tokens package never caught it
 * because its own matrix only checked `text` and `textSecondary` against card surfaces;
 * `packages/ui`'s wider matrix failed on 50 combinations.)
 */
function tertiaryFor(ground: SchemeGround): string {
  const { top, bottom } = surfaceBand(ground);
  const backgroundLum = relativeLuminance(ground.background);
  // The deepest the compost ground ever goes, at the highest decor level.
  const deepestDeep = luminanceForContrast(backgroundLum, DEEP_RATIO[4], ground.away);

  // The hardest ground for text is the one nearest the text's own value: for dark text on
  // a light scheme that is the darkest ground; for light text on a dark scheme, the lightest.
  const hardest =
    ground.away === 'darker'
      ? Math.min(bottom, deepestDeep, backgroundLum)
      : Math.max(top, deepestDeep, backgroundLum);

  return atLuminance(
    ground.textTertiary,
    luminanceForContrast(hardest, TARGET.text, ground.away),
  );
}

// ---------------------------------------------------------------------------
// Which fills a boundary is painted on (RULINGS R99, R101)
// ---------------------------------------------------------------------------

/**
 * EVERY FILL THIS THEME PAINTS. The list a boundary solve has to be stated against.
 *
 * THE DEFECT THIS EXISTS TO MAKE IMPOSSIBLE, three times over. The same mistake has now been
 * found three times in this package, each time one fill further out:
 *
 *   1. D23 / R90 — `border` and `borderStrong` were solved against `background` alone. In the
 *      dark scheme `surface` and `surfaceRaised` are LIGHTER than the page, so the hairline
 *      measured **2.74:1 on `surface` and 2.19:1 on `surfaceRaised`** (re-measured from
 *      `git show HEAD:packages/tokens/src/theme.ts`; R90 recorded 2.47 for the second figure and
 *      R99 corrects it — the ordinary `border` was worse still, at **1.22:1**).
 *   2. R99 — the fix enumerated FOUR fills. The token is painted on nine. `LoopCard` draws the
 *      capsule photo's hairline inside a `<Card tone="temperature">`, and in dark
 *      `temperature.warm.surface` sits 2.4× lighter than `surfaceRaised`, the hardest ground
 *      that enumeration considered: **1.98:1 at contrast 0**, measured in the live DOM.
 *   3. R101 — `groundEdge` was solved against `backgroundDeep`, a fill `GroundEdge` never paints
 *      it next to: **2.51:1 dark / 2.56:1 light** against `bandColour`, its real neighbour.
 *
 * So the rule, not the token: a boundary declares the fills it can be painted on
 * (`BOUNDARY_GROUNDS`) and is solved against the HARDEST member — the one nearest the
 * boundary's own value. `packages/ui/test/boundary-grounds.test.ts` reads the component sources
 * and fails if a component paints a boundary token that has not declared the fills it may meet,
 * because the manual list is precisely what failed all three times.
 *
 * THEORETICAL, NOT REALISED, wherever a fill moves with a channel other than `contrast`. Same
 * method and same reason as `tertiaryFor`: the deep ground contributes its `DEEP_RATIO[4]`
 * extreme, the ground-edge band its fixed page-relative ratio, and the five temperature cards
 * the two extremes of `surfaceBand` (which bound all five by construction — `temperatureColours`
 * spaces them geometrically between them). A boundary that quietly changed when the user moved
 * the `colour` or `decor` dial would make the mixer dishonest.
 */
export type PaintedFill =
  | 'page'
  | 'surface'
  | 'surfaceRaised'
  | 'deepGround'
  | 'groundEdgeBand'
  | 'temperatureSurfaces'
  /**
   * `colours.accentSoft` as the celebration's warm wash (design-direction §5 "scaled by
   * Intensity… to full delight"; ADA review V2). The one place a tinted fill sits UNDER
   * content that paints fixed boundary tokens — the cairn's stones and the particle dots —
   * so it joins the registry rather than being an accent fill whose boundaries are chosen
   * at render time. Its luminance is page-relative and fixed (`TARGET.soft`), sitting
   * strictly between the page and the deep ground in both schemes, so declaring it moves
   * no existing solve — it only makes the celebration adjacency an asserted one.
   */
  | 'celebrationWash';

/**
 * `bandColour`'s ratio against the page: the deep ground taken half a `DEEP_RATIO` rung further
 * away. Page-relative and FIXED, so the band — and therefore the edge solved against it — does
 * not move with the `decor` channel.
 */
const GROUND_EDGE_BAND_RATIO = DEEP_RATIO[4] * 1.08;

/**
 * Each painted fill's luminance, or luminanceS where the fill is a range rather than a value.
 * A range contributes its extremes; the solve takes the hardest of everything.
 */
const FILL_LUMINANCES: Record<PaintedFill, (ground: SchemeGround) => readonly number[]> = {
  page: (g) => [relativeLuminance(g.background)],
  surface: (g) => [relativeLuminance(g.surface)],
  surfaceRaised: (g) => [relativeLuminance(g.surfaceRaised)],
  deepGround: (g) => [
    luminanceForContrast(relativeLuminance(g.background), DEEP_RATIO[4], g.away),
  ],
  groundEdgeBand: (g) => [
    luminanceForContrast(relativeLuminance(g.background), GROUND_EDGE_BAND_RATIO, g.away),
  ],
  temperatureSurfaces: (g) => {
    const { top, bottom } = surfaceBand(g);
    return [top, bottom];
  },
  // `accentSoft`'s solve is page-relative and fixed (`TARGET.soft`), so its theoretical
  // luminance is expressible here exactly as the deep ground's is. `away` identifies the
  // scheme: 'darker' is the light scheme (see `SchemeGround.away`).
  celebrationWash: (g) => [
    luminanceForContrast(
      relativeLuminance(g.background),
      TARGET.soft[g.away === 'darker' ? 'light' : 'dark'],
      g.away,
    ),
  ],
};

/** Every fill in the theme. A boundary painted by a generic container can meet any of them. */
const ALL_PAINTED_FILLS: readonly PaintedFill[] = [
  'page',
  'surface',
  'surfaceRaised',
  'deepGround',
  'groundEdgeBand',
  'temperatureSurfaces',
  'celebrationWash',
];

/**
 * THE ENUMERATION. Which fills each boundary token is painted on, and therefore owes 3:1.
 *
 * `border` and `borderStrong` take the whole list, and that is a structural decision rather
 * than a conservative one: both are painted by `Surface`, `Card` and `Button` — generic
 * containers whose parent ground is chosen by the caller and cannot be known here. A narrower
 * list for a token a container paints is a list that will be wrong again the next time somebody
 * nests one thing inside another. `border`'s ground set is not a guess about today's call
 * sites; it is every fill that exists.
 *
 * `groundEdge` is narrow because it is not generic: exactly one component paints it
 * (`packages/ui/src/components/GroundEdge.tsx`), always as `borderTopWidth` on a `View` whose
 * own `backgroundColor` is `bandColour`, so its lower neighbour is always the band and its
 * upper neighbour is the page or nothing at all. The band is the farther of the two from the
 * page, so solving against it satisfies the page as well.
 *
 * A temperature card's own hairline is NOT here: it is solved per card against its own surface
 * (`temperatureColours`), which is the one boundary in the system whose adjacency is known
 * exactly.
 */
export const BOUNDARY_GROUNDS: Record<
  'border' | 'borderStrong' | 'groundEdge' | 'stoneFill' | 'stoneFillStrong' | 'photoFrame',
  readonly PaintedFill[]
> = {
  border: ALL_PAINTED_FILLS,
  borderStrong: ALL_PAINTED_FILLS,
  groundEdge: ['groundEdgeBand', 'page', 'deepGround'],
  /**
   * The cairn's stones (ADA review V1). `Cairn` is a generic component: a loop card draws
   * it on a temperature surface, a celebration on `surfaceRaised`, the reflection surface
   * on `surface` — so, like `border`, the stone pair declares every fill that exists and
   * is solved against the hardest. A narrower list would be the D23/R90/R99 defect again.
   */
  stoneFill: ALL_PAINTED_FILLS,
  stoneFillStrong: ALL_PAINTED_FILLS,
  /**
   * The recognition photo's frame (RULINGS R157). Painted by `LoopCard` on the card's own
   * temperature surface today — but `LoopCard` is rendered by four surfaces and the card's
   * fill is `temperature[...]` for five temperatures, so the narrow list would be a list of
   * today's call sites rather than a fact about the token. It declares every fill, like the
   * other two graphical warm tokens above, and is solved against the hardest.
   */
  photoFrame: ALL_PAINTED_FILLS,
};

/** The hardest of a boundary's declared grounds: the one nearest the boundary's own value. */
function boundaryGroundLuminance(
  ground: SchemeGround,
  fills: readonly PaintedFill[] = BOUNDARY_GROUNDS.border,
): number {
  const candidates = fills.flatMap((fill) => [...FILL_LUMINANCES[fill](ground)]);
  return ground.away === 'darker' ? Math.min(...candidates) : Math.max(...candidates);
}

function temperatureColours(
  ground: SchemeGround,
  ramps: MaterialRamps,
  neutral: string,
  colour: IntensityLevel,
  contrast: IntensityLevel,
): Record<Temperature, TemperatureColours> {
  const { top, bottom } = surfaceBand(ground);
  const a = top + 0.05;
  const b = bottom + 0.05;
  const steps = TEMPERATURE_ORDER.length - 1;
  const hue = temperatureHue(ramps);

  const out = {} as Record<Temperature, TemperatureColours>;
  TEMPERATURE_ORDER.forEach((temperature, i) => {
    const anchor = tint(neutral, hue[temperature], colour);
    const surface = atLuminance(anchor, a * Math.pow(b / a, i / steps) - 0.05);
    out[temperature] = {
      surface,
      // The BOUNDARY rises with the contrast channel (RULINGS R10)…
      border: atContrast(anchor, surface, TEMPERATURE_BOUNDARY_CONTRAST[contrast], ground.away),
      // …and the MARK does not. It is an icon tint read like small text, so it is held at the
      // TEXT target at every setting (design-direction §3: the dial scales decorative
      // contrast only). Two adjacent lines, two different laws; the comment is the reason.
      mark: atContrast(anchor, surface, TARGET.mark, ground.away),
    };
  });
  return out;
}

function colours(
  scheme: ColourScheme,
  material: MaterialId,
  colour: IntensityLevel,
  decor: IntensityLevel,
  contrast: IntensityLevel,
): Colours {
  const m = MATERIAL_TOKENS[material];
  const g = groundFor(scheme, m);
  const neutral = m.ramps.neutral[6];
  const temperature = temperatureColours(g, m.ramps, neutral, colour, contrast);
  const accentAnchor = tint(neutral, m.ramps.accent[6], colour);

  const deep = atContrast(
    tint(neutral, decor === 0 ? g.deepHue.reference : g.deepHue.raised, colour, g.deepChroma),
    g.background,
    DEEP_RATIO[decor],
    // Light: the ground opens downward, so deeper is darker. Dark: nothing can be darker
    // than the night ground, so the sliver reads as a faint warm LIFT — which is also
    // what an ember does in the dark.
    g.away,
  );

  /**
   * `textTertiary` is SOLVED, not picked.
   *
   * The surface band is derived from `textSecondary` clearing AA on the extreme card
   * (`surfaceBand`), which means any tone weaker than secondary fails there by
   * construction. The hand-picked `slate[7]` / `slate[4]` measured 3.47–3.54:1 on
   * `temperature.warm` and on `backgroundDeep` — below AA — and the tokens package never
   * noticed because its own tests only asserted `text` and `textSecondary` against card
   * surfaces. `packages/ui`'s wider matrix caught it across 50 combinations.
   *
   * design-direction §3 says text contrast is fixed at accessible levels at EVERY
   * setting, with no carve-out for a third tone. So tertiary is computed as the weakest
   * tone that still clears AA on every ground it can legally appear on. It converges
   * slightly toward secondary, which is the correct trade under §2's tiebreaker:
   * legibility is an accessibility requirement, hierarchy is mood.
   */
  const textTertiary = tertiaryFor(g);
  const boundaryGround = boundaryGroundLuminance(g, BOUNDARY_GROUNDS.border);
  const strongBoundaryGround = boundaryGroundLuminance(g, BOUNDARY_GROUNDS.borderStrong);
  const edgeGround = boundaryGroundLuminance(g, BOUNDARY_GROUNDS.groundEdge);
  // RULINGS R157: the photo frame is drawn by one component but on a card whose fill is the
  // caller's temperature, so it takes the same every-fill ground set the generic hairlines do.
  const photoFrameGround = boundaryGroundLuminance(g, BOUNDARY_GROUNDS.photoFrame);

  // The stroke-safe accent owes two ratios at once; take whichever is more extreme.
  const markTargets = [
    luminanceForContrast(relativeLuminance(g.background), TARGET.accentMark, g.away),
    ...TEMPERATURE_ORDER.map((t) =>
      luminanceForContrast(
        relativeLuminance(temperature[t].surface),
        TARGET.accentMarkOnCard,
        g.away,
      ),
    ),
  ];

  return {
    background: g.background,
    backgroundDeep: deep,
    surface: g.surface,
    surfaceRaised: g.surfaceRaised,
    // Solved against the hardest fill either is PAINTED on — every fill in the theme, because
    // both are painted by generic containers. See `BOUNDARY_GROUNDS`, which is where the reason
    // and the three-times-repeated defect live.
    border: atLuminance(
      neutral,
      luminanceForContrast(boundaryGround, BOUNDARY_CONTRAST[contrast], g.away),
    ),
    borderStrong: atLuminance(
      neutral,
      luminanceForContrast(strongBoundaryGround, STRONG_BOUNDARY_CONTRAST[contrast], g.away),
    ),
    /**
     * The cairn's stone material (ADA review V1; design-direction §2/§3). The same solve as
     * `border`/`borderStrong` — same ladders, same hardest-ground luminance — anchored on
     * the WARM ramp through `tint()`, so the stones take the material's oak warmth as the
     * colour channel rises and collapse onto the neutral anchor at colour 0 exactly as
     * temperature surfaces do. `atLuminance` holds the hue and lands the value, so the 3:1
     * obligation is discharged identically to the neutral pair it replaces in `Cairn`.
     */
    stoneFill: atLuminance(
      tint(neutral, m.ramps.warm[6], colour),
      luminanceForContrast(boundaryGround, BOUNDARY_CONTRAST[contrast], g.away),
    ),
    stoneFillStrong: atLuminance(
      tint(neutral, m.ramps.warm[6], colour),
      luminanceForContrast(strongBoundaryGround, STRONG_BOUNDARY_CONTRAST[contrast], g.away),
    ),
    // RULINGS R157. `border`'s solve — same ladder, same hardest-ground luminance, same 3:1
    // floor at every level — anchored on the material's WARM ramp through the same `tint()`
    // every temperature surface uses. See the `Colours` member for why it is not `stoneFill`
    // reused, and `photoFrameGround` below for the ground it is actually painted on.
    photoFrame: atLuminance(
      tint(neutral, m.ramps.warm[6], colour),
      luminanceForContrast(photoFrameGround, BOUNDARY_CONTRAST[contrast], g.away),
    ),
    // Solved against the ground-edge BAND, which is the fill `GroundEdge` paints the hairline on
    // — not against `backgroundDeep`, which the hairline never touches (RULINGS R101). The band
    // is the farther of the hairline's two neighbours from the page, so the page is satisfied
    // too, and the band's ratio is page-relative and fixed, so the edge does not move with
    // `decor`.
    groundEdge: atLuminance(
      neutral,
      luminanceForContrast(edgeGround, GROUND_EDGE_CONTRAST[contrast], g.away),
    ),
    text: g.text,
    textSecondary: g.textSecondary,
    textTertiary,
    // The same solve as `textTertiary`, and it lands on the same value by construction: both
    // are "the weakest tone that still clears AA on every ground it can legally appear on".
    // It is a separate token so a component declares WHY it is using the weakest tone, and so
    // that if the two ever have to diverge the call sites already say which is which.
    textDisabled: textTertiary,
    onAccent: g.onAccent,
    accent: atContrast(accentAnchor, g.background, ACCENT_CONTRAST[scheme][colour], g.away),
    // R175: the same solve as `accent`, anchored on `neutral` instead of `accentAnchor` — see
    // the `Colours` member's own doc comment for why that makes colour level 0 byte-identical
    // to `accent` rather than merely also-achromatic.
    accentNeutral: atContrast(neutral, g.background, ACCENT_CONTRAST[scheme][colour], g.away),
    accentMark: atLuminance(
      accentAnchor,
      g.away === 'darker' ? Math.min(...markTargets) : Math.max(...markTargets),
    ),
    accentSoft: atContrast(accentAnchor, g.background, TARGET.soft[scheme], g.away),
    focusRing: g.focusRing,
    temperature,
  };
}

// ---------------------------------------------------------------------------
// Motion — "motion is meaning or it is absent" (design-direction §5)
// ---------------------------------------------------------------------------

/**
 * The MVP motion contract is exactly three moments (design-direction §5, objection 1):
 *   settle — a loop is parked. A stone placed, not a card whooshing.
 *   rise   — a loop is resumed.
 *   place  — closure: the capsule-stone joins the loop's cairn.
 * Anything else is deferrable. Nothing bounces; the easing below cannot overshoot.
 */
export type MotionMoment = 'settle' | 'rise' | 'place';

/**
 * One cubic-bezier curve. Control-point Y values must stay inside [0, 1] — that is what
 * makes the output monotone, i.e. what makes "nothing bounces" (§5) a property of the
 * type's values rather than a habit. Asserted for every moment in `test/intensity.test.ts`.
 */
export type MotionEasingCurve = readonly [number, number, number, number];

/**
 * PER-MOMENT EASING (design-direction §5: "the permitted vocabulary is small and physical,
 * drawn from the metaphor" — the metaphor is specified per moment, so the curve is too;
 * ADA review V4). One global decelerating ease-out flattened the physical vocabulary:
 * a stone dropping onto a pile that decelerates into contact reads as floating, not landing.
 *
 *   settle — a stone placed by a hand: the gentle non-overshooting ease-out it always had.
 *   rise   — a gentle rise: a soft ease-in-out, unhurried at both ends.
 *   place  — a stone joining the cairn: an accelerating ease-in that STOPS DEAD at contact.
 *            Weight is carried by acceleration then an abrupt stop — the curve ends steep
 *            on purpose, and nothing decelerates it into the pile.
 *
 * ALL THREE ARE NON-OVERSHOOTING: every Y control point is inside [0, 1], so no moment can
 * bounce whatever a caller does with it. The no-bounce test iterates this record, so a
 * fourth entry — or a curve outside the band — fails the build, not a review.
 */
const MOTION_EASING: Record<MotionMoment, MotionEasingCurve> = {
  settle: [0.22, 0.61, 0.36, 1],
  rise: [0.45, 0, 0.55, 1],
  place: [0.55, 0, 1, 1],
};

export type Motion = {
  /** True at motion level 0 and under OS reduce-motion. Meaning must survive statically. */
  reduced: boolean;
  /** Travel distance in px for the three moments. 0 when reduced. */
  amplitude: number;
  durations: Record<MotionMoment, number>;
  /** Generic crossfade. Non-zero even when reduced: opacity is the static equivalent. */
  fadeDuration: number;
  /**
   * Per-moment curves (see `MOTION_EASING`). Non-overshooting at every entry; bouncing is
   * forbidden by §5. A generic crossfade that belongs to no moment takes `easing.settle`,
   * the gentlest curve — the one every consumer read when this was a single value.
   */
  easing: Record<MotionMoment, MotionEasingCurve>;
  /** Cooling is a recede over DAYS, never animated in real time (§5). */
  animateCooling: false;
};

/**
 * The ordinary-interaction vocabulary's six entries (@carn/ui `haptics.ts` `playHaptic`).
 * Declared here rather than in @carn/ui — which depends on this package, not the other way
 * round — for the same reason `IntensityChannel` is declared one layer down from where it is
 * consumed: the token layer owns the numbers a name is attached to, so the name and the
 * numbers cannot drift into two declarations. @carn/ui imports this and re-exports it as
 * `HapticMoment`.
 */
export type HapticImpactName = 'tap' | 'selection' | 'hold' | 'settle' | 'rise' | 'place';

export type Haptics = {
  enabled: boolean;
  /** 0..1 multiplier applied to platform impact styles. */
  strength: number;
  /**
   * The Interrupt Button's pattern must be distinct and learnable BLIND
   * (design-direction §5) — pressed without looking, with gloves, in rain.
   * Two firm taps then a longer settle: nothing else in Carn uses this shape.
   * Degrades to a single long vibration on weak hardware (§5, objection 3).
   */
  interruptPattern: readonly number[];
  /**
   * The ordinary-interaction vocabulary's per-entry magnitude, in ms — SIX INDEPENDENT
   * ladders, not one categorical style bumped up or down by `strength`.
   *
   * An adversarial review of the first cut of this vocabulary (docs/DEVIATIONS.md) found that
   * a single five-valued style enum, nudged up or down a tier by `strength`, collapsed all six
   * entries onto two sensations at Intensity 1 and made `settle` land LIGHTER than `tap` at
   * Intensity 4 — the exact inversion of "a low wool-soft thud on parking" (§5). Six
   * independently-scaled ladders (`hapticsFor` below) cannot collapse into each other and
   * cannot invert, because each keeps the same ratio to the others at every level by
   * construction — the same guarantee `test/intensity.test.ts`'s monotonicity assertion gives
   * every other quantity in this file.
   *
   * A platform engine that can only pick a fixed STYLE (iOS `expo-haptics`) uses this purely as
   * a silence gate (0 at Intensity 0) and picks its style from the entry's name, not the
   * number — Apple's feedback generators expose no magnitude parameter at all, which is an
   * honest hardware limit recorded where `apps/mobile/src/native/haptics.ts` reads this field,
   * not a limit this package can paper over. A platform that CAN render a duration (Android
   * `Vibration.vibrate`) plays this number directly, the same way `interruptPattern` already
   * does.
   */
  impact: Record<HapticImpactName, number>;
};

/**
 * Celebration: proportional and FINITE — one moment, then done (design-direction §5).
 * The cairn grows by one stone; the app never throws a party that must be dismissed.
 *
 * LAW (§5): the release celebration is equal in warmth to the completion celebration.
 * Both read this same table. There is deliberately no `releaseCelebration` variant.
 */
export type Celebration = {
  kind: 'tick' | 'glow' | 'stone' | 'full';
  durationMs: number;
  /** Decorative particle count. 0 at every level that must stay still. */
  particles: number;
  /** Never true. A celebration the user must dismiss is an engagement dark pattern. */
  requiresDismissal: false;
};

/**
 * Elevation. Decorative depth only — scaled by the `decor` channel, never applied to
 * anything load-bearing for legibility (§2 tiebreaker: legibility beats mood).
 */
export type Elevation = {
  /** 0 at decor level 0: depth is carried by the border and position instead. */
  opacity: number;
  radius: number;
  offsetY: number;
  /** Grain/texture overlay opacity. Drystone matte, never gloss (§2). */
  grain: number;
  /** Contour-line motif opacity. Only where the motif carries semantics (§2, objection 3). */
  contour: number;
  /**
   * The edge of the ground below (RULINGS R56). See `GROUND_EDGE_CONTRAST`.
   *
   * The one member of this type that is NOT decor-driven, and the reason is the whole ruling:
   * the vertical axis is autism-critical (design-direction §4), so its legibility cannot be a
   * decoration that switches off at decor level 0. It is driven by `contrast`, which has a
   * floor rather than a zero, and it is listed in `REFERENCE_EXCEPTIONS` for that reason.
   */
  groundEdge: GroundEdge;
};

/** The paintable description of one ground edge. Numbers plus the solved colour. */
export type GroundEdge = {
  /** Top hairline weight, in px. Always ≥3× the ordinary boundary — this is a landmark. */
  width: number;
  /** ≥3:1 against `colours.backgroundDeep`. Same value as `colours.groundEdge`. */
  colour: string;
  /** The inset band under the hairline: a second, wider statement of the same step. */
  bandHeight: number;
  /**
   * The band's fill: the deep ground moved one further step away from the page, so the band
   * reads as the layer's own shadow rather than as a second line.
   */
  bandColour: string;
};

// ---------------------------------------------------------------------------
// The reference rendering, and the deltas from it
// ---------------------------------------------------------------------------

/**
 * The scalar tokens at LEVEL 0. This object is the spec (design-direction §3 objection 2:
 * "the low-stim end is the *reference* rendering, the vivid end is the derivation").
 *
 * The interrupt haptic is described as three numbers rather than an array because its
 * shape — tap, gap, tap, gap, settle — is fixed and only its magnitudes move.
 */
export type ReferenceTokens = {
  motion: { amplitude: number; durations: Record<MotionMoment, number>; fadeDuration: number };
  /**
   * `tap`/`gap`/`settle` here are the INTERRUPT pattern's own three magnitudes (two firm taps
   * then a settle) — not the ordinary vocabulary's `tap`/`settle` entries, which live in
   * `impact` below under their own names so the two cannot be confused for each other.
   */
  haptics: {
    strength: number;
    tap: number;
    gap: number;
    settle: number;
    impact: Record<HapticImpactName, number>;
  };
  celebration: { durationMs: number; particles: number };
  /**
   * `groundEdge` is excluded: it is driven by the `contrast` channel, not by `decor`, and it
   * has a floor rather than a zero, so it is not "the reference times a multiplier". Recorded
   * in `REFERENCE_EXCEPTIONS` rather than smuggled into these tables as a zero it can never be.
   */
  elevation: Omit<Elevation, 'groundEdge'>;
};

export const REFERENCE_TOKENS: ReferenceTokens = {
  // Still, silent, flat. The only non-zero value is the crossfade: opacity is the static
  // equivalent of movement, so it survives reduced motion by design (§5).
  motion: { amplitude: 0, durations: { settle: 0, rise: 0, place: 0 }, fadeDuration: 120 },
  haptics: {
    strength: 0,
    tap: 0,
    gap: 0,
    settle: 0,
    impact: { tap: 0, selection: 0, hold: 0, settle: 0, rise: 0, place: 0 },
  },
  celebration: { durationMs: 400, particles: 0 },
  elevation: { opacity: 0, radius: 0, offsetY: 0, grain: 0, contour: 0 },
};

/**
 * ONE STEP away from the reference. Every level 1–4 is `reference + step × multiplier`.
 * Nothing below is a level's value; they are all deltas, which is the property that makes
 * a change to `REFERENCE_TOKENS` propagate by construction instead of by memory.
 */
export const STEP_TOKENS: ReferenceTokens = {
  motion: { amplitude: 4, durations: { settle: 240, rise: 200, place: 260 }, fadeDuration: 20 },
  haptics: {
    strength: 0.4,
    tap: 24,
    gap: 60,
    settle: 90,
    /**
     * One step of each ordinary-vocabulary entry, in ms, scaled by the SAME
     * `STEP_MULTIPLIERS.hapticStrength` ladder `strength` itself uses (`hapticsFor` below) —
     * so ratio between entries is fixed at every level and none can drift into another's
     * range. Ordered `selection < rise < tap < hold < settle < place` at every level: lightest
     * value-changed tick first, the most substantial non-interrupt haptic last, and `settle`
     * (a "wool-soft thud", §5) deliberately heavier than `tap` (an ordinary press) to close
     * the inversion the first cut of this vocabulary shipped with.
     */
    impact: { selection: 2.4, rise: 3.6, tap: 4, hold: 6.4, settle: 8, place: 12 },
  },
  celebration: { durationMs: 100, particles: 6 },
  elevation: { opacity: 0.04, radius: 4, offsetY: 1, grain: 0.01, contour: 0.05 },
};

/**
 * How many steps each level is worth, per quantity. Level 0 is always 0 — that is what
 * makes level 0 the reference. Every row is monotonically non-decreasing, and
 * `test/intensity.test.ts` asserts that over the resolved tokens rather than over this
 * table, so a row cannot be quietly inverted.
 */
const STEP_MULTIPLIERS = {
  motionAmplitude: { 0: 0, 1: 1, 2: 2, 3: 3, 4: 4.5 },
  motionDuration: { 0: 0, 1: 1, 2: 1.333, 3: 1.667, 4: 1.917 },
  motionFade: { 0: 0, 1: 1, 2: 2, 3: 3, 4: 4 },
  hapticStrength: { 0: 0, 1: 1, 2: 1.5, 3: 2, 4: 2.5 },
  hapticTap: { 0: 0, 1: 1, 2: 1.25, 3: 1.417, 4: 1.667 },
  // The gap between the two taps is the part of the pattern that must stay learnable
  // blind, so it does not scale (§5: "distinct and learnable blind").
  hapticGap: { 0: 0, 1: 1, 2: 1, 3: 1, 4: 1 },
  hapticSettle: { 0: 0, 1: 1, 2: 1.333, 3: 1.667, 4: 2 },
  celebrationTime: { 0: 0, 1: 2, 2: 5, 3: 8, 4: 12 },
  celebrationParticles: { 0: 0, 1: 0, 2: 0, 3: 1, 4: 3 },
  elevationOpacity: { 0: 0, 1: 1, 2: 1.75, 3: 2.5, 4: 3.5 },
  elevationRadius: { 0: 0, 1: 1, 2: 2.5, 3: 4, 4: 5.5 },
  elevationOffset: { 0: 0, 1: 1, 2: 2, 3: 3, 4: 4 },
  elevationGrain: { 0: 0, 1: 1, 2: 2, 3: 3.5, 4: 5 },
  elevationContour: { 0: 0, 1: 1, 2: 1.8, 3: 2.6, 4: 3.6 },
} satisfies Record<string, Record<IntensityLevel, number>>;

/**
 * The places a level genuinely cannot be "the reference times a number", each with
 * its own test in `test/intensity.test.ts`. Declared here so the exception list is data
 * rather than an unwritten habit.
 */
export const REFERENCE_EXCEPTIONS = [
  // A colour is not a scalar. Monotonic contrast is asserted instead — see ACCENT_CONTRAST.
  'colours.accent',
  // Categorical: 'tick' | 'glow' | 'stone' | 'full' cannot be interpolated.
  'celebration.kind',
  // Boolean gates, not magnitudes: level 0 is a different rendering, not a smaller one.
  'motion.reduced',
  'haptics.enabled',
  /**
   * The `contrast` channel has a FLOOR, not a zero (RULINGS R10, design-direction §3
   * objection 2): every level of `BOUNDARY_CONTRAST`, `STRONG_BOUNDARY_CONTRAST`,
   * `TEMPERATURE_BOUNDARY_CONTRAST`, `GROUND_EDGE_CONTRAST`, `BOUNDARY_WIDTH` and
   * `FOCUS_WIDTH` already clears WCAG 1.4.11 or a real 2px ring, including level 0. So "the
   * reference times a multiplier" would be zero, and zero is a WCAG failure. Monotonicity is
   * asserted over the resolved tokens instead — see `test/contrast.test.ts`.
   */
  'colours.border',
  'colours.borderStrong',
  'colours.groundEdge',
  // The stone pair (ADA review V1) shares `border`/`borderStrong`'s floor-not-zero reason.
  'colours.stoneFill',
  'colours.stoneFillStrong',
  // The photo frame (RULINGS R157) is `border`'s solve on the warm ramp — same floor, same reason.
  'colours.photoFrame',
  'colours.temperature.border',
  'space.hairline',
  'space.focusWidth',
  'elevation.groundEdge',
] as const;

const CELEBRATION_KIND: Record<IntensityLevel, Celebration['kind']> = {
  0: 'tick',
  1: 'tick',
  2: 'glow',
  3: 'stone',
  4: 'full',
};

const step = (base: number, unit: number, mult: number) => base + unit * mult;
const round4 = (n: number) => Math.round(n * 10000) / 10000;

function motionFor(level: IntensityLevel): Omit<Motion, 'easing' | 'animateCooling'> {
  const r = REFERENCE_TOKENS.motion;
  const s = STEP_TOKENS.motion;
  const d = STEP_MULTIPLIERS.motionDuration[level];
  return {
    reduced: level === 0,
    amplitude: step(r.amplitude, s.amplitude, STEP_MULTIPLIERS.motionAmplitude[level]),
    durations: {
      settle: Math.round(step(r.durations.settle, s.durations.settle, d)),
      rise: Math.round(step(r.durations.rise, s.durations.rise, d)),
      place: Math.round(step(r.durations.place, s.durations.place, d)),
    },
    fadeDuration: Math.round(
      step(r.fadeDuration, s.fadeDuration, STEP_MULTIPLIERS.motionFade[level]),
    ),
  };
}

/**
 * EXPORTED, unlike its `motionFor`/`celebrationFor`/`elevationFor` siblings, because one caller
 * legitimately needs haptics for a level THAT IS NOT YET THE THEME'S: the Intensity dial itself
 * (`packages/ui/src/components/IntensityDial.tsx`). A `Stop` press previews the level it is
 * ABOUT to select, not the one still resolved from the pre-press settings — `theme.haptics`
 * only updates after the state write re-renders, so a dial that reads it directly for its own
 * preview buzz plays the OLD strength on the way to a new value (found by an adversarial
 * review, docs/DEVIATIONS.md). `haptics` is priced from `level` alone — no colour, scheme or
 * material dependency — so resolving it standalone for a hypothetical level is exact, not an
 * approximation.
 */
export function hapticsFor(level: IntensityLevel): Haptics {
  const r = REFERENCE_TOKENS.haptics;
  const s = STEP_TOKENS.haptics;
  const tap = Math.round(step(r.tap, s.tap, STEP_MULTIPLIERS.hapticTap[level]));
  const gap = Math.round(step(r.gap, s.gap, STEP_MULTIPLIERS.hapticGap[level]));
  const settle = Math.round(step(r.settle, s.settle, STEP_MULTIPLIERS.hapticSettle[level]));
  // The ordinary vocabulary's six ladders — same multiplier as `strength` itself, so every
  // entry keeps a fixed ratio to every other at every level (see `Haptics.impact`).
  const impactMultiplier = STEP_MULTIPLIERS.hapticStrength[level];
  const impact = (name: HapticImpactName): number =>
    Math.round(step(r.impact[name], s.impact[name], impactMultiplier));
  return {
    enabled: level > 0,
    strength: round4(step(r.strength, s.strength, STEP_MULTIPLIERS.hapticStrength[level])),
    interruptPattern: level === 0 ? [] : [tap, gap, tap, gap, settle],
    impact: {
      tap: impact('tap'),
      selection: impact('selection'),
      hold: impact('hold'),
      settle: impact('settle'),
      rise: impact('rise'),
      place: impact('place'),
    },
  };
}

function celebrationFor(level: IntensityLevel): Celebration {
  const r = REFERENCE_TOKENS.celebration;
  const s = STEP_TOKENS.celebration;
  return {
    kind: CELEBRATION_KIND[level],
    durationMs: Math.round(
      step(r.durationMs, s.durationMs, STEP_MULTIPLIERS.celebrationTime[level]),
    ),
    particles: Math.round(
      step(r.particles, s.particles, STEP_MULTIPLIERS.celebrationParticles[level]),
    ),
    requiresDismissal: false,
  };
}

function elevationFor(level: IntensityLevel): Omit<Elevation, 'groundEdge'> {
  const r = REFERENCE_TOKENS.elevation;
  const s = STEP_TOKENS.elevation;
  return {
    opacity: round4(step(r.opacity, s.opacity, STEP_MULTIPLIERS.elevationOpacity[level])),
    radius: Math.round(step(r.radius, s.radius, STEP_MULTIPLIERS.elevationRadius[level])),
    offsetY: Math.round(step(r.offsetY, s.offsetY, STEP_MULTIPLIERS.elevationOffset[level])),
    grain: round4(step(r.grain, s.grain, STEP_MULTIPLIERS.elevationGrain[level])),
    contour: round4(step(r.contour, s.contour, STEP_MULTIPLIERS.elevationContour[level])),
  };
}

/**
 * The ground edge, assembled (RULINGS R56).
 *
 * `bandColour` is the deep ground taken one further step away from the page — half a
 * `DEEP_RATIO` rung — so the band is the same material, deeper, rather than a second hairline
 * in a different colour. In the dark scheme "away" is LIGHTER, which keeps the warm lift §2
 * authorises and still produces a legible step, because the step is now carried by weight.
 */
function groundEdgeFor(
  ground: SchemeGround,
  contrast: IntensityLevel,
  deep: string,
  edgeColour: string,
): GroundEdge {
  return {
    width: GROUND_EDGE_WIDTH[contrast],
    colour: edgeColour,
    bandHeight: GROUND_EDGE_BAND[contrast],
    bandColour: atContrast(deep, ground.background, GROUND_EDGE_BAND_RATIO, ground.away),
  };
}

// ---------------------------------------------------------------------------
// Typography, spacing, density
// ---------------------------------------------------------------------------

/**
 * THE FAMILY IS A READING LEVER, AND THE DEFAULT IS STILL THE SINGLE HUMANIST SANS
 * (design-direction §4; docs/DEVIATIONS.md F1).
 *
 * §4 asks for two things at once, and only the first was built for a long time:
 *
 *   "a highly legible humanist sans as the SINGLE UI FAMILY (evaluation candidates for the
 *    design team; no display serif, no novelty face) ... with user-adjustable size and an
 *    optional dyslexia-oriented setting (letter spacing, weight, AND FAMILY ALTERNATES —
 *    evidence on dedicated 'dyslexia fonts' is mixed, so offer the levers rather than
 *    promising the cure)."
 *
 * So "family alternates" is named by §4 in the same breath as letter spacing and weight, both
 * of which shipped. `sans` remains the single UI family and the shipped default — that is the
 * part §4 objection 2 defends, and nothing here moves it. What is added is the alternate the
 * lever sentence authorises, reachable only from the reading section, chosen only by a user
 * who went looking. See docs/DEVIATIONS.md F1 for the objection/resolution, and
 * docs/OPEN-QUESTIONS.md for the residual call that belongs to the ND panel
 * (`RULINGS.md` Part 9 question 4).
 *
 * `ReadingFamily` itself is declared once, in `@carn/core` beside `LABEL_DENSITIES` and the
 * rest of the settings vocabulary, for the reason R29 gives about intensity channels: a
 * settings union with two declarations is a union that eventually disagrees with itself, and
 * an unknown family must not be able to persist silently. This file owns only the RENDERING
 * of those names.
 *
 * @see READING_FAMILIES in `@carn/core`
 */

/**
 * The platform a family name is being resolved for.
 *
 * IT HAS TO BE AN INPUT, BECAUSE A CSS FONT STACK IS NOT A REACT NATIVE `fontFamily`.
 * On the web a comma-separated stack is the whole point — the browser walks it. On iOS and
 * Android `fontFamily` takes exactly ONE registered family name and a string it cannot
 * resolve falls back to the system face silently. That silent fallback is why `sans` looked
 * correct on device while shipping a stack: the fallback and the intent happened to agree.
 * They would NOT agree for `serif`, where a stack would resolve to nothing and the user's
 * chosen alternate would render as the sans they were trying to leave — a control that lies.
 */
export type FontPlatform = 'ios' | 'android' | 'web';

/**
 * The family name for each platform, for when the bundled face IS registered.
 *
 * `sans` is **Lexend** (docs/DEVIATIONS.md F3). design-direction §7 leaves "typeface
 * selection" explicitly open to the founder/design team, and `docs/OPEN-QUESTIONS.md` carried
 * it as an inherited item; F3 is that item being answered. It replaces a stack naming `Inter`
 * that was never bundled and therefore never rendered — the app has been drawing the system
 * sans on every platform since the token system was written.
 *
 * `serif` is deliberately the PLATFORM's own serif rather than a bundled brand face: §4's
 * "no display serif, no novelty face" governs Carn's typographic voice, and borrowing the
 * reader's own system serif takes no such decision — it is the same class of lever as
 * letter spacing, not a second brand.
 *
 * `dyslexic` is OpenDyslexic and `comic` is **Comic Neue**, both bundled under the OFL and
 * registered by `apps/mobile` under exactly these names (`apps/mobile/src/theme/fonts.ts`).
 * Comic Neue is the OFL Comic Sans-alike; Comic Sans MS itself is Microsoft's and cannot be
 * shipped, so it is neither bundled nor named — the option says what it actually draws.
 */
const FAMILY_NAMES: Record<ReadingFamily, Record<FontPlatform, string>> = {
  sans: {
    web: 'Lexend, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    ios: 'Lexend',
    android: 'Lexend',
  },
  /**
   * The reader's own interface font (docs/DEVIATIONS.md F4). Nothing is bundled and nothing can
   * fail to resolve, so it has no `FAMILY_FALLBACK` entry and is ready from the first frame.
   *
   * This is what `sans` silently resolved to before F3 bundled Lexend, so it is not a new
   * rendering — it is the old one, now reachable on purpose instead of by accident.
   */
  system: {
    web: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    ios: 'System',
    android: 'sans-serif',
  },
  /**
   * The reader's own platform serif (docs/DEVIATIONS.md F1 objection 3 — "the `serif` option
   * resolves to the reader's own platform serif… a user borrows a face their device already
   * has") — and on iOS that is **Charter**, not Georgia, because of docs/RULINGS.md R152.
   *
   * WHY THE FACE CHANGED. R152 requires numerals that sit still in every family. Shaped through
   * CoreText at 100pt on iOS 18.1, Georgia's `000`…`999` span 55.22pt and do not move under
   * `tnum` — the worst of any serif measured — so `serif` was the one family whose numerals could
   * only be fixed by drawing them in a SECOND face. Charter's span is 0.00pt: all ten digits are
   * 55.62pt wide, with no feature involved, in Roman, Bold, Italic and Bold Italic. It ships in
   * `/System/Library/Fonts/AppFonts/Charter.ttc` on iOS (verified against the 18.1, 18.5 and 26.5
   * simulator runtimes) and `UIFont(name: "Charter")` resolves to `Charter-Roman`, so this is a
   * face the device already has, exactly as F5 objection 3 requires — Carn still ships no serif
   * and still bundles nothing here.
   *
   * It is also the LESSER deviation. Satisfying §4's tabular clause by choosing a platform serif
   * that already has tabular figures keeps §4's single-family clause intact for this family,
   * where the substitute face spends FD1's licence to break it. And it is not a new choice so
   * much as an alignment: Charter has always been the head of the web stack below.
   *
   * Georgia leaves the web stack for the same reason, so that every face this cascade NAMES is
   * uniform-figured — Charter and Iowan Old Style both measure 0.00 span. Where a browser has
   * none of them it falls through to its own generic serif, which is a tail no cascade controls.
   */
  serif: {
    web: 'Charter, "Bitstream Charter", "Iowan Old Style", serif',
    // Both ship with the OS, so neither can fail to resolve.
    ios: 'Charter',
    android: 'serif',
  },
  dyslexic: {
    web: 'OpenDyslexic',
    ios: 'OpenDyslexic',
    android: 'OpenDyslexic',
  },
  comic: {
    web: '"Comic Neue"',
    ios: 'Comic Neue',
    android: 'Comic Neue',
  },
};

/**
 * What to draw while a bundled face is not registered yet.
 *
 * NATIVE FALLS BACK SILENTLY AND THAT IS THE HAZARD, NOT THE FEATURE. iOS and Android take
 * exactly one family name and quietly substitute the system face for one they cannot resolve,
 * so an unloaded `comic` would render as the sans with the settings screen still showing
 * "Comic Neue" selected. Naming the fallback here makes the substitution deliberate and
 * identical on every platform instead of an accident of the OS.
 *
 * `serif` has no entry because nothing is bundled for it — the OS already has the face.
 * The web column is absent for the same reason everywhere: a CSS stack carries its own
 * fallbacks and the browser walks them, so readiness cannot be observed there.
 */
const FAMILY_FALLBACK: Partial<Record<ReadingFamily, Record<'ios' | 'android', string>>> = {
  sans: { ios: 'System', android: 'sans-serif' },
  dyslexic: { ios: 'System', android: 'sans-serif' },
  comic: { ios: 'System', android: 'sans-serif' },
};

/**
 * design-direction §4's single UI family, as selected under §7 (docs/DEVIATIONS.md F3).
 *
 * `tabularFeature` IS REQUESTED AND, IN THE BUNDLED FACES, NOT HONOURED. §4 asks for
 * "numbers and times in tabular figures throughout: time-blind users deserve numerals that
 * sit still", and this is the request — `Text` puts it on anything with a digit in it. What
 * changed with F3 is whether anything answers, and the honest answer is a third one that
 * neither previous version of this comment allowed for. Parsing the shipped binaries with
 * `fontTools`:
 *
 *   - **Lexend and Comic Neue have no numeric features at all** — no `tnum`, `pnum`, `lnum` or
 *     `onum` in GSUB. Nothing to enable. This half of F1 objection 4 stands.
 *   - **OpenDyslexic-Regular.otf and -Bold.otf DO carry `tnum`**, in GSUB feature → lookup 25,
 *     mapping `zero`→`zero.tf` through `nine`→`nine.tf`. The substituted glyphs have advances
 *     byte-identical to the originals and still non-uniform: 664/580/639/640/665/632/664/628/
 *     682/663 per 1000em in the Regular. **The feature is present and it is a no-op.** So the
 *     old byte search was right about presence and wrong about effect; F1's correction of it
 *     was right about effect and wrong about presence; and the fact both of them missed is that
 *     a font can ship the table and still not sit still.
 *
 * That last point falsifies R152 build consequence 3 — "starts working for free if a bundled
 * face ever ships the table" — for the one bundled face that already ships it. Raised in
 * `docs/OPEN-QUESTIONS.md`; it is not something an implementing agent may amend a ruling over.
 *
 * THE REQUEST STAYS AND IS NO LONGER THE WHOLE ANSWER (docs/RULINGS.md R152,
 * docs/DEVIATIONS.md FD1). It stays because it is free, because it is what makes the tabular
 * figures of a family that HAS them actually come out (see `familyHasTabularFigures`), and
 * because a future revision of any bundled face that ships the table starts working through
 * it without a code change. What R152 adds is the case it cannot cover: where the chosen
 * family has no table to enable, the numerals themselves change face — see
 * `resolveNumericFamily`.
 */
export const FONT_STACK = {
  sans: FAMILY_NAMES.sans.web,
  /** Same family, tabular figures enabled by the caller via fontVariant. */
  tabularFeature: ['tabular-nums'] as const,
} as const;

/**
 * Does this family's OWN rendering put numerals on a uniform advance, ON THIS PLATFORM?
 * (docs/RULINGS.md R152, docs/DEVIATIONS.md FD1.)
 *
 * ONE RULE, EVALUATED PER FAMILY AND PER PLATFORM — not a blanket second face. A family that
 * answers `true` keeps its own numerals and never sees the substitute, so the reader who chose
 * it gets the single-family rendering design-direction §4 preferred; a family that answers
 * `false` has `resolveNumericFamily` draw its numeral tokens in a face that does sit still.
 *
 * IT IS PER-PLATFORM BECAUSE THE ANSWER GENUINELY IS. `serif` is not one typeface: it is
 * whatever the reader's device calls a serif, and the three of them do not agree. An earlier
 * version of this table was one boolean per family and paid for it by substituting for `serif`
 * on platforms whose serif needs no substitute — which is a second face on screen bought for
 * nothing, against the clause §4 gave up to get here.
 *
 * MEASURED, AND THIS TIME THROUGH THE SHAPER. Advances come from `CTLineGetTypographicBounds`
 * over `000`…`999` at 100pt on an iOS 18.1 simulator, with and without the `tnum` feature
 * applied through the same `UIFontDescriptor` route `fontVariant: ['tabular-nums']` takes; the
 * bundled faces are registered from `apps/mobile/assets/fonts` so they are the shipped binaries.
 * Reading `hmtx` directly is NOT enough and was the previous error: a feature substitutes glyphs
 * during shaping, so the advance of the base glyph says nothing about what the feature does.
 * (`span` below is widest minus narrowest; 0.00 is uniform.)
 *
 *   face                   lineHeight   span plain   span +tnum
 *   Lexend                   125.000       36.00        36.00
 *   OpenDyslexic             182.000       30.60        30.60
 *   Comic Neue               115.000       63.90        63.90
 *   Georgia                  113.623       55.22        55.22
 *   Charter                  122.021        0.00         0.00
 *   Noto Serif (Android)      —           see below     see below
 *   Roboto (Android)          —           see below     see below
 *   San Francisco            119.336       52.15         0.00
 *
 * The two Android faces are the honest exception and are marked as such rather than folded in:
 * there is no Android simulator here, so they are read out of the shipped Google Fonts binaries
 * with `fontTools` — all ten digits at 559 per 1000em in Noto Serif and 562 in Roboto, uniform in
 * `hmtx` before any feature, which is the one case where `hmtx` IS sufficient (nothing has to be
 * substituted for a face whose base glyphs already agree). What that cannot rule out is an OEM
 * ROM aliasing `serif` to something other than Noto Serif.
 *
 *   - `sans`, `dyslexic`, `comic` — **false everywhere.** These are the three bundled faces and
 *     the reason FD1 exists. Note OpenDyslexic: it DOES carry a `tnum` feature in GSUB (lookup
 *     25, `zero`→`zero.tf` … `nine`→`nine.tf`), and the substituted glyphs have byte-identical,
 *     still non-uniform advances — 664/580/639/640/665/632/664/628/682/663 per 1000em, before
 *     and after. The feature exists and is a **no-op**, which is a third possibility neither the
 *     old byte search nor F1's correction of it allowed for. It also means R152 build consequence
 *     3 — "starts working for free if a bundled face ever ships the table" — is already false for
 *     this one face: it ships the table and nothing happens. Raised in `docs/OPEN-QUESTIONS.md`.
 *   - `serif` — **true everywhere, and no longer the interesting one**, because the iOS face
 *     changed to the one the web stack already led with. Charter is uniform at 166.85 for all
 *     ten digits with no feature involved, on iOS and on the web; Android's `serif` is Noto
 *     Serif, measured uniform at 559 for all ten in the shipped Google Fonts binary. Georgia,
 *     which iOS used to draw here, is the single worst serif for this — span 55.22 and it
 *     ignores `tnum` — and is the reason the substitute used to fire. See `FAMILY_NAMES.serif`.
 *   - `system` — **true, on every platform.** San Francisco needs the request and answers it
 *     (52.15 → 0.00); Roboto is uniform in the metrics table before any feature. RN plumbs
 *     `fontVariant` to both platforms.
 */
const FAMILY_TABULAR_FIGURES: Record<ReadingFamily, Record<FontPlatform, boolean>> = {
  sans: { web: false, ios: false, android: false },
  serif: { web: true, ios: true, android: true },
  dyslexic: { web: false, ios: false, android: false },
  comic: { web: false, ios: false, android: false },
  system: { web: true, ios: true, android: true },
};

/** @see FAMILY_TABULAR_FIGURES */
export function familyHasTabularFigures(family: ReadingFamily, platform: FontPlatform): boolean {
  return FAMILY_TABULAR_FIGURES[family][platform];
}

/**
 * The `fontFamily` to draw NUMERALS in, for a chosen `family` on `platform`
 * (docs/RULINGS.md R152, docs/DEVIATIONS.md FD1 — design-direction §4's "single UI family"
 * clause is dropped by founder decision where it collides with the same section's tabular
 * requirement).
 *
 * It equals `resolveFontFamily(...)` whenever the chosen family's own numerals are already
 * tabular, so a `system` reader never sees a second face and neither does anyone else once a
 * bundled face ships the table. Only a family without one substitutes.
 *
 * THE SUBSTITUTE IS THE `system` FAMILY, WHICH IS WHY THERE IS NO NEW FONT FILE. The one
 * family already proven to carry real tabular figures on iOS, Android and the web is the
 * reader's own interface face, and it is already in `FAMILY_NAMES` — so this derives from
 * that entry rather than naming a face of its own. Three consequences, all of them the
 * reason it was chosen over a monospace (Menlo/Courier are on the device too, and are
 * uniform by construction):
 *
 *   1. Nothing is bundled, so nothing can fail to load and there is no `FAMILY_FALLBACK`
 *      entry to model. The substitute is ready on the first frame on every platform.
 *   2. San Francisco and Roboto are humanist sans figures. Set beside Lexend inside a
 *      sentence they read as numerals in a sentence; Menlo reads as a code listing, and §4's
 *      "typography disappears into legibility" survives the first choice and not the second.
 *   3. It is the face the app already draws for the `system` reader, so the substitution
 *      introduces no typeface to Carn that Carn did not already ship.
 *
 * AND A FOURTH, WHICH IS THE ONE THAT NEARLY MADE IT THE WRONG CHOICE — the vertical axis.
 * RN 0.86.2's `RCTAttributedTextUtils.mm` (`RCTApplyBaselineOffsetForRange`, lines 300-339)
 * sets ONE `NSBaselineOffsetAttributeName` per `Text` node: `(maximumLineHeight −
 * MAX(font.lineHeight over the whole range)) / 2`. Putting a second face into a node can only
 * raise that MAX, and raising it lowers the offset — which drops the WHOLE node relative to a
 * digit-free sibling in the same row. Nothing on the web can show this: CSS inline layout
 * shares one baseline across faces, so every screenshot in `screenshots/` is structurally
 * blind to it. Measured `UIFont.lineHeight` at 100pt on iOS 18.1, the shift is
 * `MAX(0, SF − family) / 2` per em:
 *
 *   family         lineHeight   shift/em   at body 17pt   at title 28pt   at body × 2.5
 *   Lexend           125.000     0.0000        0.00pt          0.00pt          0.00pt
 *   OpenDyslexic     182.000     0.0000        0.00pt          0.00pt          0.00pt
 *   Charter          122.021     n/a — `serif` no longer substitutes at all
 *   Comic Neue       115.000     0.0217        0.37pt          0.61pt          0.92pt
 *   (Georgia)        113.623     0.0286        0.49pt          0.80pt          1.21pt
 *
 * So two of the three substituting families are EXACTLY ZERO — San Francisco is shorter than
 * both, so the MAX never moves — and `serif` is zero because Charter needs no substitute.
 * `comic` is the whole residual, and it is a floor rather than a choice: of every uniform-figured
 * face on iOS, San Francisco is the SHORTEST (SF 119.336; Charter 122.021, Verdana 121.533,
 * Iowan Old Style 136.523, Avenir Next 136.600), while every face shorter than Comic Neue that
 * looked like a candidate — Helvetica 115.000, Arial 111.719, Times New Roman 110.742 — turns
 * out NOT to sit still when shaped (spans 14.75, 14.84, 7.42), which is why it must be measured
 * through `CTLine` and not read out of `hmtx`. Any other substitute makes the shift larger.
 * Against that 0.37pt of vertical drift, Comic Neue's own numerals span 63.90pt per 100pt —
 * 10.9pt of horizontal jitter at body size, thirty times the residual, in the axis the reader
 * is travelling along. Recorded in `docs/OPEN-QUESTIONS.md` rather than smoothed over.
 *
 * `ready` is accepted and forwarded for the family's own case only. The substitute never
 * needs it: nothing is bundled for `system`.
 */
export function resolveNumericFamily(
  family: ReadingFamily,
  platform: FontPlatform,
  ready = true,
): string {
  if (familyHasTabularFigures(family, platform)) return resolveFontFamily(family, platform, ready);
  return FAMILY_NAMES.system[platform];
}

/**
 * The `fontFamily` string to render `family` with on `platform`.
 *
 * `ready` says whether the bundled face for this family is registered yet. It defaults to
 * `true` so a pure caller — a test, a preview, anything that is not the running app — gets
 * the intended name rather than having to model a loader it does not have.
 */
export function resolveFontFamily(
  family: ReadingFamily,
  platform: FontPlatform,
  ready = true,
): string {
  if (!ready && platform !== 'web') {
    const fallback = FAMILY_FALLBACK[family];
    if (fallback) return fallback[platform];
  }
  return FAMILY_NAMES[family][platform];
}

export type TypeStyle = {
  fontSize: number;
  lineHeight: number;
  fontWeight: '400' | '500' | '600' | '700';
  letterSpacing: number;
};

export type Typography = {
  family: string;
  /**
   * The face NUMERALS are drawn in (docs/RULINGS.md R152, docs/DEVIATIONS.md FD1).
   *
   * Equal to `family` — string-identical, so a caller can compare and do nothing — whenever
   * the chosen family's own numerals are tabular. It differs only for a family with no
   * `tnum` table, and then only digit runs take it: prose stays in `family`, because the
   * clause §4 gave up was "one family", not "one face per string".
   *
   * @see resolveNumericFamily
   */
  numericFamily: string;
  tabularFeature: readonly string[];
  /** Screen title. */
  title: TypeStyle;
  /** A loop's name on the resumption surface — the most-read string in the app. */
  loopTitle: TypeStyle;
  /** "next: get the 13mm from the jacket" */
  nextAction: TypeStyle;
  body: TypeStyle;
  /** "3 loops cooling — look when you're ready" */
  quiet: TypeStyle;
  label: TypeStyle;
  /**
   * SUPPORTING METADATA — the floor of the scale, and nothing smaller may be added
   * (docs/DEVIATIONS.md D44).
   *
   * For facts that ANNOTATE the thing rather than being it: when a finished loop ended, the
   * temperature word on a card's badge. Never for a loop's own words — a title, a capsule line,
   * a voice transcript and a hard commitment are content and stay in the tiers above.
   *
   * design-direction §4 asks for "generous default size and line height", and this tier is the
   * one deliberate exception, bought by the same section's core layout tension — "visible enough
   * to exist, quiet enough not to shout". It buys quiet by SIZE only: tone, contrast obligation
   * (12px is normal text, so AA 4.5:1 applies exactly as it does to body) and both user reading
   * levers are untouched, so a reader who needs this bigger turns `textScale` up and takes the
   * whole scale with them.
   */
  supporting: TypeStyle;
  /** Times, counts, durations. Always tabular. */
  numeric: TypeStyle;
};

/** Dyslexia-oriented levers, not promises (design-direction §4). */
export type ReadingPreferences = {
  /** User text-size multiplier, on top of OS scaling. */
  textScale: number;
  /** Extra letter spacing in px. */
  letterSpacing: number;
  /** Extra line-height multiplier. */
  lineHeightBoost: number;
  /** Heavier default weight. */
  bolderText: boolean;
  /**
   * §4's "family alternates" lever. `'sans'` is the single UI family and the default;
   * the alternates exist for the reader, not for the brand (docs/DEVIATIONS.md F1).
   */
  fontFamily: ReadingFamily;
};

export const DEFAULT_READING: ReadingPreferences = {
  textScale: 1,
  letterSpacing: 0,
  lineHeightBoost: 1,
  bolderText: false,
  // The law's default, not a caller's to move: design-direction §4 objection 2 keeps the
  // single humanist sans as the shipped face, and F1 changes only what a user may reach.
  fontFamily: 'sans',
};

/**
 * The bounds a reading preference may take. `textScale: 0` produced `fontSize: 0` — an
 * invisible app — and `textScale: -2` produced `fontSize: -42`.
 */
export const READING_BOUNDS = {
  textScale: { min: 0.85, max: 2.5 },
  letterSpacing: { min: 0, max: 4 },
  lineHeightBoost: { min: 1, max: 2 },
} as const;

function clampNumber(value: unknown, min: number, max: number, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}

/**
 * Clamp any stored/synced reading preference back into a renderable range.
 *
 * The counterpart of `normaliseIntensity`, and it exists for the same stated reason:
 * "sync from another device or a future app version must never produce an unrenderable"
 * setting. It was applied to one of the two settings groups and not the other, so a
 * slider bug or a bad sync could set the whole app's font size to zero.
 * `resolveTheme` calls it unconditionally.
 */
export function normaliseReading(input: unknown): ReadingPreferences {
  const raw = (input ?? {}) as Partial<ReadingPreferences>;
  const b = READING_BOUNDS;
  return {
    textScale: clampNumber(raw.textScale, b.textScale.min, b.textScale.max, 1),
    letterSpacing: clampNumber(raw.letterSpacing, b.letterSpacing.min, b.letterSpacing.max, 0),
    lineHeightBoost: clampNumber(
      raw.lineHeightBoost,
      b.lineHeightBoost.min,
      b.lineHeightBoost.max,
      1,
    ),
    bolderText: raw.bolderText === true,
    // Same posture as every clamp above: a family arriving from sync or from a future app
    // version that this build does not know how to render falls back to the default face
    // rather than reaching `fontFamily` as a string nothing can resolve.
    fontFamily: isReadingFamily(raw.fontFamily) ? raw.fontFamily : 'sans',
  };
}

function typography(
  reading: ReadingPreferences,
  platform: FontPlatform,
  fontsReady: readonly ReadingFamily[] | undefined,
): Typography {
  const s = reading.textScale;
  const lh = reading.lineHeightBoost;
  const ls = reading.letterSpacing;
  const w = (base: TypeStyle['fontWeight']): TypeStyle['fontWeight'] => {
    if (!reading.bolderText) return base;
    return base === '400' ? '500' : base === '500' ? '600' : '700';
  };
  const style = (
    fontSize: number,
    lineRatio: number,
    fontWeight: TypeStyle['fontWeight'],
    extraLs = 0,
  ): TypeStyle => ({
    fontSize: Math.round(fontSize * s),
    lineHeight: Math.round(fontSize * s * lineRatio * lh),
    fontWeight: w(fontWeight),
    letterSpacing: ls + extraLs,
  });

  // `undefined` means "no loader is reporting", which is every caller that is not the
  // running app — see `resolveFontFamily`'s `ready` default.
  const ready = fontsReady === undefined || fontsReady.includes(reading.fontFamily);

  return {
    family: resolveFontFamily(reading.fontFamily, platform, ready),
    /**
     * §4's "numbers and times in tabular figures throughout" holds across the swap **in
     * `@carn/ui`'s `Text`, and only there** — but NOT, as this comment used to claim, because
     * "every family offered carries a `tnum` feature". Measured, three of the five do not
     * (docs/DEVIATIONS.md FD1). It holds because the numerals of a family without the table are
     * drawn in one that has it, and it holds only for as long as both halves stay: the request
     * below, and the face here.
     *
     * WHERE IT DOES NOT HOLD, SAID PLAINLY RATHER THAN CLAIMED AWAY: every text INPUT in the app
     * sets `type.family` raw, with neither `tabularFeature` nor this face — `settings/controls`,
     * `capture/CaptureSurface`, `interrupt/InterrupterPrompt`, `sorting/SortingSession`,
     * `loop/LoopDetail`. A `Text` can substitute per-token because it can nest; a `TextInput`
     * draws one string in one face and has nowhere to put a second one, so the seam does not
     * reach it. A user typing "Get 13mm bolts" in Lexend reads it back on the acknowledgement
     * card with `13mm` in San Francisco, one tap apart. Raised in `docs/OPEN-QUESTIONS.md`; it
     * needs a ruling, not a patch.
     */
    numericFamily: resolveNumericFamily(reading.fontFamily, platform, ready),
    tabularFeature: FONT_STACK.tabularFeature,
    // Generous default size and line height (§4).
    title: style(28, 1.25, '600', -0.2),
    loopTitle: style(21, 1.35, '600'),
    nextAction: style(17, 1.5, '500'),
    body: style(17, 1.55, '400'),
    quiet: style(15, 1.5, '400'),
    label: style(13, 1.4, '500', 0.3),
    // One step below `label`, and shaped like it rather than like `quiet`: the weight and the
    // extra letter-spacing are what keep 12px legible, and the slightly looser line ratio is
    // what stops a two-line date wrapping tight (D44). Everything else about it — the family,
    // both reading levers, the tabular request — is the same machinery every tier above uses.
    supporting: style(12, 1.45, '500', 0.3),
    numeric: style(15, 1.4, '500'),
  };
}

/**
 * Density. Default is radically sparse (design-direction §4). Higher densities are
 * opt-in — some ADHD users genuinely prefer everything-visible, because hiding can
 * mean ceasing-to-exist.
 *
 * IT GOVERNS QUANTITY AS WELL AS SPACING, AND IT DID NOT USED TO (docs/DEVIATIONS.md Q1).
 * §4's sentence is about how much the surface SHOWS — "the 'Where was I?' surface shows at
 * most the warm set, full stop... higher densities are opt-in" — and for a long time this
 * type reached nothing but `cardGap`/`cardPadding`/`screenPadding` below, while the
 * settings surface offered it under labels ("Show everything") that promised content.
 * `DENSITY_NAMED_LOOPS` is the quantity half; `DENSITY` is the spacing half.
 */
export type Density = 'sparse' | 'balanced' | 'full';

export type Spacing = {
  /** 4pt base scale. */
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
  xxl: number;
  /** Gap between loop cards on the resumption surface. */
  cardGap: number;
  /** Inner padding of a loop card. */
  cardPadding: number;
  screenPadding: number;
  /**
   * THE FIELD'S OWN ROOM ABOVE THE PLATE (`RULINGS R189`; docs/DEVIATIONS.md `now-page/NT4`).
   *
   * The height of card surface a treatment keeps for itself, measured from the card's top edge —
   * so `Surface` spends it as `fieldBand - gutter` on the plate's `marginTop`, the gutter having
   * already paid the first part of it.
   *
   * NAMED BECAUSE IT IS LOAD-BEARING IN TWO FILES AND WAS A BARE EXPRESSION IN ONE. It was
   * `theme.space.xxl * 2` inline in `Surface`, chosen — R189's comment says so — because it "is the
   * same height the field had as a band, so this change moves the copy rather than growing the
   * decoration". That is a migration-neutrality argument, not a derivation, and the number it
   * produced is what `field.ts`' `FIELD_CORE_DEEPEST` has to clear for the composition to be
   * visible at all. Two files, one relationship, previously no link between them.
   *
   * WHAT IT COSTS, WHICH WAS RECORDED NOWHERE. A treatment adds exactly `fieldBand + md -
   * cardPadding` = 56pt to every card, because the card's own padding drops to the gutter when a
   * field draws. On the documented 320×568 fold arithmetic (`R108`, `D45`, `H4`) the leading card's
   * primary action clears the fold by 17–21pt, so a treated card puts it BELOW the fold. R189
   * accepts that a treatment user "is choosing a taller card"; it does not record that this is what
   * they are choosing. Raised in docs/OPEN-QUESTIONS.md — lowering this is a look decision, and
   * `FIELD_CORE_DEEPEST` has to come down with it or the core goes back behind the plate.
   */
  fieldBand: number;
  radius: { sm: number; md: number; lg: number; pill: number };
  /**
   * Glove-friendly minimum (implementation-brief §11: "all touch targets ≥ the
   * design system's glove-friendly minimum"). WCAG 2.5.5 asks 44; we ask 56,
   * because the reference user is wearing gloves in the rain.
   *
   * DERIVED from the resolved body line height, never a bare constant: a fixed 56 next to
   * a 2.5× text scale means a label that no longer fits the target it labels, and nothing
   * in the type system notices. It can only grow — 56 is a floor, at any density.
   */
  minTarget: number;
  /** The Interrupt Button. Pressed without looking. Always larger than `minTarget`. */
  interruptTarget: number;
  /**
   * Waymark discipline: ONE stroke weight across the whole icon system (§6).
   *
   * NOT scaled by the `contrast` channel, and that is deliberate: §6's "a single consistent
   * stroke weight" is the discipline the whole icon set is drawn to, and a dial that thickened
   * every glyph would break it. Boundaries scale (`hairline`); strokes do not.
   */
  strokeWidth: number;
  /**
   * Component-boundary weight. SCALED BY THE `contrast` CHANNEL (RULINGS R10) — every card,
   * row, checkbox outline and divider in the app reads this, so one table moves all of them.
   * Never below 1px: level 0 is the reference rendering, not the absence of a boundary.
   */
  hairline: number;
  /**
   * Focus-ring weight. Scaled by `contrast`; the ring's COLOUR is fixed at every setting
   * (RULINGS R61). Floor of 2px, because a focus ring is how a switch-control user knows
   * where they are.
   */
  focusWidth: number;
};

/**
 * The spacing half of density. DELIBERATELY IN THE SAME DIRECTION AS THE QUANTITY HALF:
 * more things on screen, less air around each.
 *
 * The numbers are unchanged and the reasoning is now written down, because it used to be an
 * accident. `sparse` is the roomiest and `full` is the tightest, which was internally
 * coherent ("sparse means fewer things, so more air around each") and was labelled
 * "Compact" → "Everything" on the settings surface — so choosing *Compact* made every card
 * BIGGER (docs/DEVIATIONS.md Q1). With quantity now riding the same axis, the two halves
 * agree: at `full` the quiet rows name loops they used only to count, and the tighter
 * spacing is what buys the room to show them. A user who asks for more on screen and gets
 * more air is being argued with.
 *
 * WHAT THIS TABLE MAY NEVER TOUCH. `minTarget` is computed below from the resolved body
 * line height and `MIN_TOUCH_TARGET`, and reads nothing from here — 56 is a floor at every
 * density (CLAUDE.md; implementation-brief §11), asserted in
 * `packages/tokens/test/intensity.test.ts` and `packages/ui/test/contrast.test.ts` for all
 * three densities. Text contrast is not on this axis either: design-direction §3 fixes it
 * at accessible levels at every setting, and nothing here is a colour.
 */
const DENSITY: Record<Density, Pick<Spacing, 'cardGap' | 'cardPadding' | 'screenPadding'>> = {
  sparse: { cardGap: 16, cardPadding: 20, screenPadding: 20 },
  balanced: { cardGap: 12, cardPadding: 16, screenPadding: 16 },
  full: { cardGap: 8, cardPadding: 12, screenPadding: 12 },
};

/**
 * The quantity half of density: how many of the loops a quiet count row also NAMES beneath
 * itself (`QuietRow`, @carn/ui).
 *
 * Authorised by design-direction §4 — "a user-controlled density setting exists (some ADHD
 * users genuinely prefer everything-visible, because hiding can mean ceasing-to-exist), but
 * higher densities are opt-in and never the default" — and by that section's Critic Record
 * **objection 1**, the document's own "strongest objection": *"radical sparsity hides
 * information, and out-of-sight is out-of-mind for exactly this audience."* Partially upheld;
 * the stated resolution names **the quiet count row** as the mechanism that preserves
 * presence, and hands the design team the tension to hold: *"visible enough to exist, quiet
 * enough not to shout."* So the count row is the one honest place for this setting to change
 * how much is shown, and it is the only place it does.
 *
 * `sparse: 0` IS THE REFERENCE RENDERING AND IS NOT A NUMBER TO TUNE. It is
 * product-concept §6.3 verbatim — "Below the warm set, one quiet row: '3 loops cooling —
 * look when you're ready.' Nothing else." — and design-direction §3 objection 2 makes the
 * low-stim end the rendering every other is a delta from. The default must render exactly
 * as it did before this table existed.
 *
 * THE HIGHER NUMBERS ARE SMALL ON PURPOSE, and small is the argument. product-concept §8.3
 * forbids a "wall of their own unmet intentions" as the default visual experience, and the
 * resting surface is one tap from that default; the quiet rows are also fixed furniture
 * (`ResumptionSurface` rule 2), so every named line is a line the warm card does not get on
 * a 320×568 phone (docs/RULINGS.md R108, docs/DEVIATIONS.md D16/H4 — the fold budget there
 * is already 22pt over). Naming a few is presence; naming all of them is the wall, rebuilt
 * in the one place §6.3 said to keep clear. The count line always states the true total, so
 * nothing is ever misrepresented as complete.
 */
export const DENSITY_NAMED_LOOPS: Record<Density, number> = {
  sparse: 0,
  balanced: 2,
  full: 4,
};

/** The absolute floor, at every density and every text scale. */
export const MIN_TOUCH_TARGET = 56;

function spacing(density: Density, type: Typography, contrast: IntensityLevel): Spacing {
  const md = 12;
  const minTarget = Math.max(MIN_TOUCH_TARGET, Math.round(type.body.lineHeight + 2 * md));
  return {
    xs: 4,
    sm: 8,
    md,
    lg: 16,
    xl: 24,
    xxl: 32,
    ...DENSITY[density],
    // EQUAL TO THE GUTTER, WHICH MEANS THERE IS NO RESERVED BAND AT ALL (docs/RULINGS.md R195).
    // `Surface` spends this as `fieldBand - gutter` on the plate's `marginTop`, so making the two
    // equal makes that 0: the field is the card's BACKGROUND, visible as a uniform mount on all four
    // sides, rather than a block of graphics above the copy. That is what `R189` asked for in words —
    // "the content sits on a smaller rounded plate floating inside it, so background is visible on all
    // four sides" — and what the 8pt gutter was too thin to deliver. See `Spacing.fieldBand`.
    fieldBand: 12,
    radius: { sm: 6, md: 12, lg: 18, pill: 999 },
    minTarget,
    interruptTarget: Math.max(88, Math.round(minTarget * 1.5)),
    strokeWidth: 1.75,
    hairline: BOUNDARY_WIDTH[contrast],
    focusWidth: FOCUS_WIDTH[contrast],
  };
}

// ---------------------------------------------------------------------------
// Sound — silent by default (design-direction §5)
// ---------------------------------------------------------------------------

export type SoundPreferences = {
  /** Off. Always off, until the user asks. Many autistic users are harmed by uninvited audio. */
  enabled: boolean;
  volume: number;
};

export const DEFAULT_SOUND: SoundPreferences = { enabled: false, volume: 0.5 };

// ---------------------------------------------------------------------------
// The resolved theme
// ---------------------------------------------------------------------------

export type ThemeInput = {
  intensity: IntensityPreferences;
  scheme: ColourScheme;
  /**
   * The hue-anchor set to render (docs/DEVIATIONS.md M11). Defaults to Drystone, which is the
   * free material and the design system's reference rendering.
   *
   * THE CALLER PASSES THE *EFFECTIVE* MATERIAL, NOT THE STORED CHOICE. Entitlement is not this
   * function's business — `@carn/core`'s `effectiveMaterial(chosen, tier)` is the one place a
   * material choice meets a tier, and `packages/ui`'s `ThemeProvider` calls it before it gets
   * here. A theme resolver that knew about billing would be a theme resolver two features from
   * now that decides what a user may read.
   */
  material?: MaterialId;
  density?: Density;
  reading?: ReadingPreferences;
  system?: SystemAccessibility;
  /** product-concept §11: swaps playful copy for precise functional labels. */
  literal?: boolean;
  /**
   * design-direction §6, CLAUDE.md: how `Waymark`/`Button` render glyph + word. Defaults
   * to `'icon-and-words'` — the law's default, not a caller's to override silently.
   */
  labels?: LabelDensity;
  /**
   * The surface treatment (`RULINGS R188`). Defaults to `'matte'` — the pre-treatment rendering,
   * so an existing caller that does not pass it resolves exactly what it resolved before.
   *
   * NO ENTITLEMENT SEAM, unlike `material` above, and deliberately: `@carn/core`'s
   * `treatments.ts` records why a paid treatment would break `docs/DEVIATIONS.md` M11's premise
   * and unprice the four paid materials. There is nothing here for a tier to gate.
   */
  treatment?: Treatment;
  /**
   * Which platform's font names to resolve `reading.fontFamily` to (docs/DEVIATIONS.md F1).
   *
   * Defaults to `'web'`, which is the string this resolver has always produced, so an
   * existing caller that does not pass it renders exactly what it rendered before.
   * `packages/ui`'s `ThemeProvider` passes `Platform.OS` — it is the one place in the system
   * that both builds the app's theme and is allowed to import from react-native, which is
   * why the platform enters here as an input rather than being sniffed inside a pure package.
   */
  platform?: FontPlatform;
  /**
   * Which bundled faces are registered right now (docs/DEVIATIONS.md F3).
   *
   * A FACT ABOUT THE RUNTIME, NOT A PREFERENCE — it never persists and the user never sets
   * it. `apps/mobile` loads Lexend at start and the alternates on demand, and passes the list
   * through `ThemeProvider`; when a load resolves, this changes, which produces a new `Theme`,
   * which is what repaints a tree that had already laid itself out in the fallback face.
   *
   * Omitted means "nothing is reporting", and every family resolves to its intended name. That
   * is the right answer for tests, previews and the web build, where a CSS stack carries its
   * own fallbacks and readiness is not observable.
   */
  fontsReady?: readonly ReadingFamily[];
};

export type Theme = {
  scheme: ColourScheme;
  /** The material actually rendered. Already resolved against entitlement by the caller. */
  material: MaterialId;
  density: Density;
  /** Effective per-channel levels after overrides and OS accessibility. */
  levels: Record<IntensityChannel, IntensityLevel>;
  /**
   * Channels the OS took out of the user's hands. Render `intensity.systemOverride` for
   * these rather than a control that fights back. Empty unless `input.system` said so —
   * these clamps are never written into the persisted override map.
   *
   * Covers the READING channels as well as the Intensity ones (RULINGS R11): `bolderText` and
   * `textScale` are not dial channels, but the OS clamps them in exactly the same way, and a
   * "Bolder text" switch that silently refuses to turn off is the same failure as a motion
   * slider that will not rise. `ClampedChannel` is the union; a consumer that only cares about
   * dial channels filters `INTENSITY_CHANNELS`, which is what `SystemClampNotice` does.
   */
  systemClamped: ReadonlySet<ClampedChannel>;
  colours: Colours;
  type: Typography;
  space: Spacing;
  motion: Motion;
  haptics: Haptics;
  celebration: Celebration;
  elevation: Elevation;
  /** Normalised reading preferences actually in force. */
  reading: ReadingPreferences;
  /**
   * The bundled faces registered when this theme was resolved (`ThemeInput.fontsReady`).
   *
   * Carried on the Theme so a nested resolve — `ContrastRegion` re-resolves the WHOLE theme —
   * can inherit it instead of being plumbed separately and disagreeing with its own parent
   * about which faces exist. `undefined` means nothing was reporting.
   */
  fontsReady?: readonly ReadingFamily[];
  /** OS "Reduce Transparency": draw fills opaque, blur nothing. Touches no channel. */
  opaqueSurfaces: boolean;
  /** Convenience mirror of ThemeInput.literal for components that branch on register. */
  literal: boolean;
  /**
   * design-direction §6, CLAUDE.md. `Waymark` and `Button` read this to decide whether to
   * show the glyph, the word, or both — so a screen never prop-drills the setting.
   */
  labels: LabelDensity;
  /**
   * The surface treatment in force (`RULINGS R188`). `LoopCard` reads this and `levels.decor`
   * and asks `resolveField` for its geometry, so a screen never prop-drills the setting — the
   * same contract `labels` works under one field up.
   */
  treatment: Treatment;
};

export function resolveTheme(input: ThemeInput): Theme {
  const system = input.system ?? {};
  const applied = applySystemAccessibility(normaliseIntensity(input.intensity), system);
  const levels = resolveChannels(applied.prefs);
  const density = input.density ?? 'sparse';
  // Clamped for the same reason `normaliseIntensity` is: a material arriving from sync or from a
  // future app version must never produce an unrenderable theme (see `normaliseMaterial`).
  const material = normaliseMaterial(input.material);

  let reading = normaliseReading(input.reading ?? DEFAULT_READING);
  // ReadingPreferences sits "on top of OS scaling" (§4), so the OS has to be representable.
  if (system.boldText) reading = { ...reading, bolderText: true };
  /**
   * THE REAL DYNAMIC TYPE MULTIPLIER, AS A FLOOR (RULINGS R11; ADA review X1).
   *
   * `system.fontScale` is `PixelRatio.getFontScale()` itself. It used to arrive as a boolean
   * and floor `textScale` at 1.15 — so a user at iOS accessibility sizes (~2.0–3.1×) got
   * 1.15× from the theme while RN's default `allowFontScaling` multiplied the full OS factor
   * on top of the themed size, bypassing every 250%-textScale layout proof. The primitives
   * now set `allowFontScaling={false}` (recorded in docs/DEVIATIONS.md), so ALL scaling flows
   * through here: a floor, never a replacement — the user's own larger value wins — clamped
   * to `READING_BOUNDS.textScale.max` because 2.5× is the largest rendering every surface is
   * proven at, and `systemClamped` names the channel (see `applySystemAccessibility`) so the
   * settings row can explain itself instead of fighting back.
   */
  const osFontScale = system.fontScale ?? 1;
  if (osFontScale > 1) {
    reading = {
      ...reading,
      textScale: Math.max(
        reading.textScale,
        Math.min(osFontScale, READING_BOUNDS.textScale.max),
      ),
    };
  }

  const motion: Motion = {
    ...motionFor(levels.motion),
    // Per-moment, not global (design-direction §5; ADA review V4) — see `MOTION_EASING`.
    easing: MOTION_EASING,
    animateCooling: false,
  };

  /**
   * Particles ARE motion, so `celebration` is resolved AGAINST `motion` — for the user's
   * own mixer as well as for the OS switch. `{ dial: 0, overrides: { celebration: 4 } }`
   * used to resolve to reduced motion with eighteen particles: the code already knew the
   * combination was invalid, because the OS path clamped it, and enforced it only there.
   *
   * PARTICLES ARE ZEROED. `kind` IS NOT — and it used to be (RULINGS R170 clause 3,
   * docs/DEVIATIONS.md FD2 objection 3).
   *
   * `'full'` was downgraded to `'glow'` here, which was right while `'full'` meant "the glow,
   * plus eighteen decorative specks": specks are motion, they carry no meaning, and dropping
   * them costs a reduced-motion user nothing. R170 changed what `'full'` MEANS. It is now the
   * cairn drawn as it is built, ending on a golden capstone — and the meaning it carries ("it
   * is built") has an exact static equivalent, which is the completed pile with that cap, at
   * rest. So the downgrade stopped being a removal of decoration and became the removal of a
   * whole channel of meaning: `'glow'` renders no cairn at all (`Celebration.tsx`'s
   * `showStone`). That is the DOWNGRADE §5 forbids, arriving through the line whose own
   * comment forbade it.
   *
   * WHICH USER THIS REACHES, STATED EXACTLY, BECAUSE AN EARLIER VERSION OF THIS COMMENT GOT IT
   * WRONG. This clamp fires on `motion.reduced`, which is true for the user's own mixer at
   * `motion: 0` AND for the OS Reduce Motion switch — but the OS path never arrives here with
   * `kind === 'full'`, because `applySystemAccessibility` (./intensity.ts) has already clamped
   * the CELEBRATION channel to 1, and `CELEBRATION_KIND[1]` is `'tick'`. So the rendering this
   * line restores is reachable through the in-app mixer and not through the OS switch. That is
   * a narrower repair than "reduced motion is full parity" implies, it is not this function's
   * to widen — the celebration clamp is disclosed, shipped policy with its own settings surface
   * (`SystemClampNotice`) and R170 says nothing about it — and the gap is on the docket at
   * `docs/OPEN-QUESTIONS.md`, not papered over here.
   *
   * `'full'` therefore survives reduced motion, and `packages/ui/src/components/Cairn.tsx`
   * draws the built cairn at rest instead of building it. The clamp keeps doing the one job
   * it was added for: nobody buys particles without motion. Recorded in docs/DEVIATIONS.md
   * (`CB1`), with the provenance of the original downgrade — which had none.
   */
  const base = celebrationFor(levels.celebration);
  const celebration: Celebration = motion.reduced ? { ...base, particles: 0 } : base;

  const type = typography(reading, input.platform ?? 'web', input.fontsReady);
  const resolvedColours = colours(
    input.scheme,
    material,
    levels.colour,
    levels.decor,
    levels.contrast,
  );
  const space = spacing(density, type, levels.contrast);

  return {
    scheme: input.scheme,
    material,
    density,
    levels,
    systemClamped: applied.systemClamped,
    colours: resolvedColours,
    type,
    space,
    motion,
    haptics: hapticsFor(levels.haptics),
    celebration,
    elevation: {
      ...elevationFor(levels.decor),
      // Contrast-driven, not decor-driven — see `Elevation.groundEdge` (RULINGS R56).
      groundEdge: groundEdgeFor(
        groundFor(input.scheme, MATERIAL_TOKENS[material]),
        levels.contrast,
        resolvedColours.backgroundDeep,
        resolvedColours.groundEdge,
      ),
    },
    reading,
    fontsReady: input.fontsReady,
    opaqueSurfaces: system.reduceTransparency === true,
    literal: input.literal ?? false,
    // The law's default (design-direction §6, CLAUDE.md), not a caller's silent override.
    labels: input.labels ?? 'icon-and-words',
    // `normaliseTreatment` for the same reason `normaliseMaterial` is called above: a value
    // arriving from sync or from a future app version must never produce an unrenderable theme.
    treatment: normaliseTreatment(input.treatment),
  };
}

// ---------------------------------------------------------------------------
// The boundary adjacency matrix, derived (RULINGS R99, R101)
// ---------------------------------------------------------------------------

export type BoundaryToken =
  | 'border'
  | 'borderStrong'
  | 'groundEdge'
  // The cairn's stones are graphical objects painted on a caller-chosen ground (ADA review
  // V1), so they carry the same 3:1-everywhere obligation the generic hairlines do.
  | 'stoneFill'
  | 'stoneFillStrong'
  // The recognition photo's warm frame (RULINGS R157): a hairline round an image, drawn on a
  // caller-chosen card fill, so it owes the same 3:1 everywhere the generic hairlines do.
  | 'photoFrame';

/**
 * Every fill the theme paints, REALISED, keyed by the same names the solve is stated in.
 *
 * This is the other half of `BOUNDARY_GROUNDS`: the solve uses the theoretical luminances so a
 * boundary does not drift with `colour` or `decor`, and a *measurement* needs the hexes that
 * actually ship. Both read the same registry, so a test cannot measure a pairing the solve does
 * not know about, and cannot miss one it does.
 */
export function paintedFills(theme: Theme): Record<PaintedFill, readonly string[]> {
  return {
    page: [theme.colours.background],
    surface: [theme.colours.surface],
    surfaceRaised: [theme.colours.surfaceRaised],
    deepGround: [theme.colours.backgroundDeep],
    groundEdgeBand: [theme.elevation.groundEdge.bandColour],
    temperatureSurfaces: TEMPERATURE_ORDER.map((t) => theme.colours.temperature[t].surface),
    // The celebration's warm wash (ADA review V2): `accentSoft` painted as a ground.
    celebrationWash: [theme.colours.accentSoft],
  };
}

/**
 * EVERY (boundary, adjacent fill) PAIR THE THEME PRODUCES, derived from `BOUNDARY_GROUNDS`
 * rather than restated.
 *
 * The restated list is exactly what failed three times (see `BOUNDARY_GROUNDS`): a test that
 * enumerates its own pairs certifies the enumeration, not the theme. Every WCAG 1.4.11 assertion
 * in `packages/tokens/test` and `packages/ui/test` iterates this function, so widening a token's
 * ground set widens the assertion in the same commit, and narrowing one narrows it visibly.
 *
 * The five temperature hairlines are included because they are boundaries too — theirs is the one
 * adjacency in the system that is known exactly (a card's hairline against its own fill), so it
 * is stated here as a pair of one rather than through the registry.
 */
export function boundaryAdjacencies(
  theme: Theme,
): Array<{ what: string; boundary: string; fill: string }> {
  const fills = paintedFills(theme);
  const out: Array<{ what: string; boundary: string; fill: string }> = [];
  const tokens: Record<BoundaryToken, string> = {
    border: theme.colours.border,
    borderStrong: theme.colours.borderStrong,
    groundEdge: theme.colours.groundEdge,
    stoneFill: theme.colours.stoneFill,
    stoneFillStrong: theme.colours.stoneFillStrong,
    photoFrame: theme.colours.photoFrame,
  };
  for (const token of Object.keys(tokens) as BoundaryToken[]) {
    for (const fillName of BOUNDARY_GROUNDS[token]) {
      fills[fillName].forEach((fill, i) => {
        const index = fills[fillName].length > 1 ? `[${TEMPERATURE_ORDER[i]}]` : '';
        out.push({ what: `${token} on ${fillName}${index}`, boundary: tokens[token], fill });
      });
    }
  }
  // `elevation.groundEdge.colour` is the same token reached by another route; asserted separately
  // so a future divergence between the two is caught rather than assumed away.
  for (const fill of fills.groundEdgeBand) {
    out.push({
      what: 'elevation.groundEdge.colour on groundEdgeBand',
      boundary: theme.elevation.groundEdge.colour,
      fill,
    });
  }
  for (const temperature of TEMPERATURE_ORDER) {
    out.push({
      what: `${temperature} hairline on its own card`,
      boundary: theme.colours.temperature[temperature].border,
      fill: theme.colours.temperature[temperature].surface,
    });
  }
  return out;
}
